import React, { useEffect, useState } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { SketchPicker } from "react-color";
import NumberFormat from "react-number-format";
import reactCSS from "reactcss";
import { reorderArray } from "../../../config/utils";
import { BbiTableConfig } from "../../smartCharts/components/BbiConfig/BbiTableConfig";
import Portal from "../../common/components/Portal";

const BsiForm = ({
    bsiProperty = {},
    handleBsiChange,
    FONT_FAMILY = [],
    setBbiBsiTableColumnConfig,
    setBsiTableColumnConfig,
    onBSiColumnOrderChange,
    ResetToDefault,
    hasBandValueChanged,
    type = "bsi"
}) => {
    const styles = reactCSS({
        default: {
            color: {
                width: "40px",
                height: "15px",
                borderRadius: "3px",
                zoom: "107%"
            },
            popover: {
                position: "absolute",
                zIndex: "3",
                left: "111px",
                top: "-76px"
            },
            cover: {
                position: "fixed",
                top: "0px",
                right: "0px",
                bottom: "0px",
                left: "0px"
            },
            swatch: {
                padding: "6px",
                background: "#ffffff",
                borderRadius: "2px",
                cursor: "pointer",
                display: "inline-block",
                boxShadow: "0 0 0 1px rgba(0,0,0,.2)"
            }
        }
    });
    const [colorModalState, setColorModalState] = useState(null);
    const [isCustomHeading, setIsCustomHeading] = useState({
        heading: true,
        table_heading: true,
        stop_light: true,
        executive: true,
        executive_additional: true,
        executive_trade: true
    });
    const [showTableConfig, setTableConfig] = useState(false);
    const onColumnDnd = (result, type) => {
        if (!result.destination) {
            return;
        }
        const keyList = reorderArray(
            bsiProperty?.bbi_table?.bbi_table_column_configuration?.fields?.[type],
            result.source.index,
            result.destination.index
        );
        onBSiColumnOrderChange(keyList, type, "bsi");
    };
    let totalRowsForColumnDnd = bsiProperty?.bbi_table?.bbi_table_column_configuration?.fields?.building_fields?.length
        ? Math.trunc(bsiProperty?.bbi_table?.bbi_table_column_configuration?.fields?.building_fields.length / 3) +
          (bsiProperty?.bbi_table?.bbi_table_column_configuration?.fields?.building_fields.length % 3 ? 1 : 0)
        : 0;
    const defaultCustomHeading = {
        bold: false,
        color: "ffffff",
        font_size: 10,
        font_id: "",
        bg_color: "003f2d",
        alignment: "left",
        cell_margin: { top: 0.04, left: 0.04, right: 0.04, bottom: 0.04 }
    };
    useEffect(() => {
        if (bsiProperty) {
            setIsCustomHeading(prev => ({
                ...prev,
                ...(Object.keys(bsiProperty?.bbi_heading?.custom_heading_props || {}).length > 0 ? { heading: true } : { heading: false }),
                ...(Object.keys(bsiProperty?.bbi_table?.table_heading?.custom_heading_props || {}).length > 0
                    ? { table_heading: true }
                    : { table_heading: false }),
                ...(Object.keys(bsiProperty?.bbi_heading_executive_summary?.custom_heading_props || {}).length > 0
                    ? { executive: true }
                    : { executive: false }),
                ...(Object.keys(bsiProperty?.stop_light?.main_heading?.custom_heading_props || {}).length > 0
                    ? { stop_light: true }
                    : { stop_light: false }),
                ...(Object.keys(bsiProperty?.executive_summary?.additional?.custom_heading_props || {}).length > 0
                    ? { executive_additional: true }
                    : { executive_additional: false }),
                ...(Object.keys(bsiProperty?.executive_summary?.trade?.custom_heading_props || {}).length > 0
                    ? { executive_trade: true }
                    : { executive_trade: false })
            }));
        }
    }, [bsiProperty]);

    const toggleTableConfig = () => setTableConfig(!showTableConfig);

    const renderTableConfig = () => {
        if (!showTableConfig) return null;
        const { building_fields } = bsiProperty?.bbi_table?.bbi_table_column_configuration?.fields || [];
        return (
            <Portal
                body={
                    <BbiTableConfig
                        onSave={configFields => {
                            toggleTableConfig();
                            setBsiTableColumnConfig(configFields, type);
                        }}
                        fieldsConfig={building_fields}
                        onCancel={() => toggleTableConfig()}
                        isBsi={type === "bsi"}
                        isBai={type === "bai"}
                        isClientProperty
                    />
                }
                onCancel={() => toggleTableConfig()}
            />
        );
    };

    return (
        <>
            <div className="styles-box">
                {renderTableConfig()}
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {type === "bsi" ? "BSI" : "BAI"} Heading
                        <button
                            type="button"
                            className={`reset-btn-recom ${
                                hasBandValueChanged(type, ["custom_heading_props", "structured_heading"], "bbi_heading") ? "cursor-disabled" : ""
                            }`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["custom_heading_props", "structured_heading"], "bbi_heading")}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["custom_heading_props", "structured_heading"],
                                    columnKey: "bbi_heading",
                                    band: `${type === "bsi" ? "BSI" : "BAI"}  Heading`
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Heading Label</label>
                            <div className={`input-ft-size`}>
                                <input
                                    value={bsiProperty?.bbi_heading?.name || ""}
                                    type="text"
                                    onChange={e => {
                                        handleBsiChange("bbi_heading", "name", e.target?.value, null, null, type);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Heading Type</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={isCustomHeading?.heading ? "custom" : "predefined"}
                                    onChange={e => {
                                        setIsCustomHeading({ ...isCustomHeading, heading: e.target.value === "custom" });
                                        if (e.target.value === "custom") {
                                            handleBsiChange("bbi_heading", null, defaultCustomHeading, "custom_heading_props", null, type);
                                        } else {
                                            handleBsiChange("bbi_heading", null, { heading_style: "Heading 3" }, "structured_heading", null, type);
                                        }
                                    }}
                                >
                                    <option value="custom">Custom Heading</option>
                                    <option value="predefined">Predefined Heading</option>
                                </select>
                            </div>
                        </div>

                        {isCustomHeading?.heading ? (
                            <>
                                <div className="font-family font-st col-md-3">
                                    <label>Heading Font Name </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.font_id || ""}
                                            onChange={e => {
                                                handleBsiChange("bbi_heading", "font_id", e.target?.value, "custom_heading_props", null, type);
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {FONT_FAMILY.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="font-size font-st col-md-3">
                                    <label>Heading Font Size</label>

                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.font_size || 0}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.bbi_heading?.custom_heading_props?.font_size || 0;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "bbi_heading",
                                                        "font_size",
                                                        values.floatValue,
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" Pt"
                                        />
                                    </div>
                                </div>

                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Heading Font Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => handleBsiChange("bbi_heading", "color", "", "custom_heading_props", null, type)}
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "custom_heading_font_color" ? null : "custom_heading_font_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.bbi_heading?.custom_heading_props?.color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.bbi_heading?.custom_heading_props?.color
                                                        ? `#${bsiProperty?.bbi_heading?.custom_heading_props?.color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "custom_heading_font_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.bbi_heading?.custom_heading_props?.color === "#000000"
                                                            ? ""
                                                            : bsiProperty?.bbi_heading?.custom_heading_props?.color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "bbi_heading",
                                                            "color",
                                                            color.hex?.replace("#", ""),
                                                            "custom_heading_props",
                                                            null,
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Heading Font Bold </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.bold || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "bbi_heading",
                                                    "bold",
                                                    e.target?.value === "true",
                                                    "custom_heading_props",
                                                    null,
                                                    type
                                                );
                                            }}
                                        >
                                            <option value={false}>No</option>
                                            <option value={true}>Yes</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Heading Alignment</label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            name="tag"
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.alignment || ""}
                                            onChange={e =>
                                                handleBsiChange("bbi_heading", "alignment", e.target?.value, "custom_heading_props", null, type)
                                            }
                                        >
                                            <option value="">Select</option>
                                            <option value={"left"}>Left</option>
                                            <option value={"center"}>Center</option>
                                            <option value={"right"}>Right</option>
                                            <option value={"justify"}>Justify</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Heading Background Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => handleBsiChange("bbi_heading", "bg_color", "", "custom_heading_props", null, type)}
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "custom_heading_bg_color" ? null : "custom_heading_bg_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.bbi_heading?.custom_heading_props?.bg_color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.bbi_heading?.custom_heading_props?.bg_color
                                                        ? `#${bsiProperty?.bbi_heading?.custom_heading_props?.bg_color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "custom_heading_bg_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.bbi_heading?.custom_heading_props?.bg_color === "#000000"
                                                            ? ""
                                                            : bsiProperty?.bbi_heading?.custom_heading_props?.bg_color || ""
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "bbi_heading",
                                                            "bg_color",
                                                            color.hex?.replace("#", ""),
                                                            "custom_heading_props",
                                                            null,
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Heading Cell Margin Top</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.top || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.top;
                                                if (values.floatValue !== currentValue)
                                                    handleBsiChange(
                                                        "bbi_heading",
                                                        "cell_margin",
                                                        { top: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Heading Cell Margin Left</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.left || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.left;
                                                if (values.floatValue !== currentValue)
                                                    handleBsiChange(
                                                        "bbi_heading",
                                                        "cell_margin",
                                                        { left: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Heading Cell Margin Right</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.right || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.right;
                                                if (values.floatValue !== currentValue)
                                                    handleBsiChange(
                                                        "bbi_heading",
                                                        "cell_margin",
                                                        { right: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Heading Cell Margin Bottom</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.bottom || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.bbi_heading?.custom_heading_props?.cell_margin?.bottom;
                                                if (values.floatValue !== currentValue)
                                                    handleBsiChange(
                                                        "bbi_heading",
                                                        "cell_margin",
                                                        { bottom: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="font-family font-st col-md-3">
                                <label>Heading Style</label>
                                <div className="font-select">
                                    <select
                                        className="dropdown"
                                        value={bsiProperty?.bbi_heading?.structured_heading?.heading_style || ""}
                                        onChange={e =>
                                            handleBsiChange("bbi_heading", "heading_style", e.target.value, "structured_heading", null, type)
                                        }
                                    >
                                        <option value="Heading 1">Heading 1</option>
                                        <option value="Heading 2">Heading 2</option>
                                        <option value="Heading 3">Heading 3</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {type === "bsi" ? "BSI" : "BAI"} Sub Heading
                        <button
                            type="button"
                            className={`reset-btn-recom ${hasBandValueChanged(type, ["bbi_sub_heading"]) ? "cursor-disabled" : ""}`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["bbi_sub_heading"])}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["bbi_sub_heading"],
                                    band: `${type === "bsi" ? "BSI" : "BAI"}  Sub Heading`
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Sub Heading Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.bbi_sub_heading?.font_id || ""}
                                    onChange={e => {
                                        handleBsiChange("bbi_sub_heading", "font_id", e.target?.value, null, null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="font-size font-st col-md-3">
                            <label>Sub Heading Font Size</label>

                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.bbi_sub_heading?.font_size || 0}
                                    onValueChange={values => {
                                        const currentValue = bsiProperty?.bbi_sub_heading?.font_size;
                                        if (values.floatValue !== currentValue) {
                                            handleBsiChange("bbi_sub_heading", "font_size", values.floatValue, null, null, type);
                                        }
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>

                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Sub Heading Font Color </label>
                            </div>
                            <div className="dropdown">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => handleBsiChange("bbi_sub_heading", "color", "", null, null, type)}
                                >
                                    <i class="fas fa-times "></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() =>
                                        setColorModalState(prevColorModalState =>
                                            prevColorModalState === "sub_heading_font_color" ? null : "sub_heading_font_color"
                                        )
                                    }
                                >
                                    <span>{bsiProperty?.bbi_sub_heading?.color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.bbi_sub_heading?.color ? `#${bsiProperty?.bbi_sub_heading?.color}` : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "sub_heading_font_color" && (
                                    <div style={styles.popover}>
                                        <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                        <SketchPicker
                                            position="right"
                                            color={
                                                bsiProperty?.bbi_sub_heading?.color === "#000000" || !bsiProperty?.bbi_sub_heading?.color
                                                    ? ""
                                                    : bsiProperty?.bbi_sub_heading?.color
                                            }
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color =>
                                                handleBsiChange("bbi_sub_heading", "color", color.hex?.replace("#", ""), null, null, type)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Sub Heading Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.bbi_sub_heading?.bold || ""}
                                    onChange={e => {
                                        handleBsiChange("bbi_sub_heading", "bold", e.target?.value === "true", null, null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Sub Heading Alignment</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.bbi_sub_heading?.alignment || ""}
                                    onChange={e => handleBsiChange("bbi_sub_heading", "alignment", e.target?.value, null, null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>

                        <div className="font-family font-st col-md-3">
                            <label>Sub Heading Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.bbi_sub_heading?.space_before || 0}
                                    onValueChange={values => {
                                        const currentValue = bsiProperty?.bbi_sub_heading?.space_before;
                                        if (values.floatValue !== currentValue) {
                                            handleBsiChange("bbi_sub_heading", "space_before", values.floatValue, null, null, type);
                                        }
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Sub Heading Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.bbi_sub_heading?.space_after || 0}
                                    onValueChange={values => {
                                        const currentValue = bsiProperty?.bbi_sub_heading?.space_after;
                                        if (values.floatValue !== currentValue) {
                                            handleBsiChange("bbi_sub_heading", "space_after", values.floatValue, null, null, type);
                                        }
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div> */}
            <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {type === "bsi" ? "BSI" : "BAI"} Executive Summary Heading
                        <button
                            type="button"
                            className={`reset-btn-recom ${
                                hasBandValueChanged(type, ["custom_heading_props", "structured_heading"], "bbi_heading_executive_summary")
                                    ? "cursor-disabled"
                                    : ""
                            }`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["custom_heading_props", "structured_heading"], "bbi_heading_executive_summary")}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["custom_heading_props", "structured_heading"],
                                    columnKey: "bbi_heading_executive_summary",
                                    band: "Executive Summary Heading"
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>

                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Executive Summary Heading Label</label>
                            <div className={`input-ft-size`}>
                                <input
                                    value={bsiProperty?.bbi_heading_executive_summary?.name || ""}
                                    type="text"
                                    onChange={e => {
                                        handleBsiChange("bbi_heading_executive_summary", "name", e.target?.value, null, null, type);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Executive Summary Heading Type</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={isCustomHeading?.executive ? "custom" : "predefined"}
                                    onChange={e => {
                                        setIsCustomHeading({ ...isCustomHeading, executive: e.target.value === "custom" });
                                        if (e.target.value === "custom") {
                                            handleBsiChange(
                                                "bbi_heading_executive_summary",
                                                null,
                                                defaultCustomHeading,
                                                "custom_heading_props",
                                                null,
                                                type
                                            );
                                        } else {
                                            handleBsiChange(
                                                "bbi_heading_executive_summary",
                                                null,
                                                { heading_style: "Heading 3" },
                                                "structured_heading",
                                                null,
                                                type
                                            );
                                        }
                                    }}
                                >
                                    <option value="custom">Custom Heading</option>
                                    <option value="predefined">Predefined Heading</option>
                                </select>
                            </div>
                        </div>
                        {isCustomHeading?.executive ? (
                            <>
                                <div className="font-family font-st col-md-3">
                                    <label>Executive Summary Heading Font Name </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.font_id || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "bbi_heading_executive_summary",
                                                    "font_id",
                                                    e.target?.value,
                                                    "custom_heading_props",
                                                    null,
                                                    type
                                                );
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {FONT_FAMILY.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Executive Summary Heading Font Size</label>

                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.font_size || 0}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.font_size;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "bbi_heading_executive_summary",
                                                        "font_size",
                                                        values.floatValue,
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" Pt"
                                        />
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Executive Summary Heading Font Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e =>
                                                handleBsiChange("bbi_heading_executive_summary", "color", "", "custom_heading_props", null, type)
                                            }
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "bbi_heading_executive_summary_font_color"
                                                        ? null
                                                        : "bbi_heading_executive_summary_font_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.color
                                                        ? `#${bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "bbi_heading_executive_summary_font_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.color === "#000000" ||
                                                        !bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.color
                                                            ? ""
                                                            : bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "bbi_heading_executive_summary",
                                                            "color",
                                                            color.hex?.replace("#", ""),
                                                            "custom_heading_props",
                                                            null,
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Executive Summary Heading Font Bold </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.bold || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "bbi_heading_executive_summary",
                                                    "bold",
                                                    e.target?.value === "true",
                                                    "custom_heading_props",
                                                    null,
                                                    type
                                                );
                                            }}
                                        >
                                            <option value={false}>No</option>
                                            <option value={true}>Yes</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Executive Summary Heading Alignment</label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            name="tag"
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.alignment || ""}
                                            onChange={e =>
                                                handleBsiChange(
                                                    "bbi_heading_executive_summary",
                                                    "alignment",
                                                    e.target?.value,
                                                    "custom_heading_props",
                                                    null,
                                                    type
                                                )
                                            }
                                        >
                                            <option value="">Select</option>
                                            <option value={"left"}>Left</option>
                                            <option value={"center"}>Center</option>
                                            <option value={"right"}>Right</option>
                                            <option value={"justify"}>Justify</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Executive Summary Background Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e =>
                                                handleBsiChange("bbi_heading_executive_summary", "bg_color", "", "custom_heading_props", null, type)
                                            }
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "bbi_heading_executive_summary_bg_color"
                                                        ? null
                                                        : "bbi_heading_executive_summary_bg_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.bg_color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.bg_color
                                                        ? `#${bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.bg_color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "bbi_heading_executive_summary_bg_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.bg_color === "#000000"
                                                            ? ""
                                                            : bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.bg_color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "bbi_heading_executive_summary",
                                                            "bg_color",
                                                            color.hex?.replace("#", ""),
                                                            "custom_heading_props",
                                                            null,
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Executive Summary Heading Margin Top</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.top || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.top;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "bbi_heading_executive_summary",
                                                        "cell_margin",
                                                        { top: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Executive Summary Heading Margin Left</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.left || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.left;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "bbi_heading_executive_summary",
                                                        "cell_margin",
                                                        { left: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Executive Summary Heading Margin Right</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.right || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.right;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "bbi_heading_executive_summary",
                                                        "cell_margin",
                                                        { right: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Executive Summary Heading Margin Bottom</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.bottom || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.bbi_heading_executive_summary?.custom_heading_props?.cell_margin?.bottom;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "bbi_heading_executive_summary",
                                                        "cell_margin",
                                                        { bottom: values.floatValue },
                                                        "custom_heading_props",
                                                        null,
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="font-family font-st col-md-3">
                                <label>Executive Summary Heading Style</label>
                                <div className="font-select">
                                    <select
                                        className="dropdown"
                                        value={bsiProperty?.bbi_heading_executive_summary?.structured_heading?.heading_style || "Heading 3"}
                                        onChange={e =>
                                            handleBsiChange(
                                                "bbi_heading_executive_summary",
                                                "heading_style",
                                                e.target.value,
                                                "structured_heading",
                                                null,
                                                type
                                            )
                                        }
                                    >
                                        <option value="Heading 1">Heading 1</option>
                                        <option value="Heading 2">Heading 2</option>
                                        <option value="Heading 3">Heading 3</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {type === "bsi" ? "BSI" : "BAI"} Additionals (Executive Summary)
                        <button
                            type="button"
                            className={`reset-btn-recom reset-btn- ${
                                hasBandValueChanged(type, ["additional"], "executive_summary") ? "cursor-disabled" : ""
                            }`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["additional"], "executive_summary")}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["additional"],
                                    columnKey: "executive_summary",
                                    band: `${type === "bsi" ? "BSI" : "BAI"}  Additionals (Executive Summary)`
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Additionals Heading Type</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={isCustomHeading?.executive_additional ? "custom" : "predefined"}
                                    onChange={e => {
                                        setIsCustomHeading({ ...isCustomHeading, executive_additional: e.target.value === "custom" });
                                        if (e.target.value === "custom") {
                                            handleBsiChange(
                                                "executive_summary",
                                                null,
                                                defaultCustomHeading,
                                                "additional",
                                                "custom_heading_props",
                                                type
                                            );
                                        } else {
                                            handleBsiChange(
                                                "executive_summary",
                                                null,
                                                { heading_style: "Heading 3" },
                                                "additional",
                                                "structured_heading",
                                                type
                                            );
                                        }
                                    }}
                                >
                                    <option value="custom">Custom Heading</option>
                                    <option value="predefined">Predefined Heading</option>
                                </select>
                            </div>
                        </div>

                        {isCustomHeading?.executive_additional ? (
                            <>
                                <div className="font-family font-st col-md-3">
                                    <label>Additionals Heading Font Name </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.font_id || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "executive_summary",
                                                    "font_id",
                                                    e.target?.value,
                                                    "additional",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {FONT_FAMILY.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="font-size font-st col-md-3">
                                    <label>Additionals Heading Font Size</label>

                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.font_size || 0}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.executive_summary?.additional?.custom_heading_props?.font_size;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "font_size",
                                                        values.floatValue,
                                                        "additional",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" Pt"
                                        />
                                    </div>
                                </div>

                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Additionals Heading Font Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e =>
                                                handleBsiChange("executive_summary", "color", "", "additional", "custom_heading_props", type)
                                            }
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "executive_additional_heading_font_color"
                                                        ? null
                                                        : "executive_additional_heading_font_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.executive_summary?.additional?.custom_heading_props?.color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.executive_summary?.additional?.custom_heading_props?.color
                                                        ? `#${bsiProperty?.executive_summary?.additional?.custom_heading_props?.color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "executive_additional_heading_font_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.executive_summary?.additional?.custom_heading_props?.color === "#000000" ||
                                                        !bsiProperty?.executive_summary?.additional?.custom_heading_props?.color
                                                            ? ""
                                                            : bsiProperty?.executive_summary?.additional?.custom_heading_props?.color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "executive_summary",
                                                            "color",
                                                            color.hex?.replace("#", ""),
                                                            "additional",
                                                            "custom_heading_props",
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Additionals Heading Font Bold </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.bold || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "executive_summary",
                                                    "bold",
                                                    e.target?.value === "true",
                                                    "additional",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                        >
                                            <option value={false}>No</option>
                                            <option value={true}>Yes</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Additionals Heading Alignment</label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            name="tag"
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.alignment || ""}
                                            onChange={e =>
                                                handleBsiChange(
                                                    "executive_summary",
                                                    "alignment",
                                                    e.target?.value,
                                                    "additional",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                        >
                                            <option value="">Select</option>
                                            <option value={"left"}>Left</option>
                                            <option value={"center"}>Center</option>
                                            <option value={"right"}>Right</option>
                                            <option value={"justify"}>Justify</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Additionals Heading Background Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e =>
                                                handleBsiChange("executive_summary", "bg_color", "", "additional", "custom_heading_props", type)
                                            }
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "executive_additional_heading_bg_color"
                                                        ? null
                                                        : "executive_additional_heading_bg_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.executive_summary?.additional?.custom_heading_props?.bg_color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.executive_summary?.additional?.custom_heading_props?.bg_color
                                                        ? `#${bsiProperty?.executive_summary?.additional?.custom_heading_props?.bg_color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "executive_additional_heading_bg_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.executive_summary?.additional?.custom_heading_props?.bg_color === "#000000"
                                                            ? ""
                                                            : bsiProperty?.executive_summary?.additional?.custom_heading_props?.bg_color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "executive_summary",
                                                            "bg_color",
                                                            color.hex?.replace("#", ""),
                                                            "additional",
                                                            "custom_heading_props"
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Additionals Heading Margin Top</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.top}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.top;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { top: values.floatValue },
                                                        "additional",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Additionals Heading Margin Left</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.left || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.left;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { left: values.floatValue },
                                                        "additional",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Additionals Heading Margin Right</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.right || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.right;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { right: values.floatValue },
                                                        "additional",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Additionals Heading Margin Bottom</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.bottom || ""}
                                            onValueChange={values => {
                                                const currentValue =
                                                    bsiProperty?.executive_summary?.additional?.custom_heading_props?.cell_margin?.bottom;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { bottom: values.floatValue },
                                                        "additional",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="font-family font-st col-md-3">
                                <label>Additionals Heading Style</label>
                                <div className="font-select">
                                    <select
                                        className="dropdown"
                                        value={bsiProperty?.executive_summary?.additional?.structured_heading?.heading_style || "Heading 3"}
                                        onChange={e =>
                                            handleBsiChange(
                                                "executive_summary",
                                                "heading_style",
                                                e.target.value,
                                                "additional",
                                                "structured_heading",
                                                type
                                            )
                                        }
                                    >
                                        <option value="Heading 1">Heading 1</option>
                                        <option value="Heading 2">Heading 2</option>
                                        <option value="Heading 3">Heading 3</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {type === "bsi" ? "BSI" : "BAI"} Trade (Executive Summary)
                        <button
                            type="button"
                            className={`reset-btn-recom ${hasBandValueChanged(type, ["trade"], "executive_summary") ? "cursor-disabled" : ""}`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["trade"], "executive_summary")}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["trade"],
                                    columnKey: "executive_summary",
                                    band: `${type === "bsi" ? "BSI" : "BAI"}  Trade (Executive Summary)`
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Trade Heading Type</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={isCustomHeading?.executive_trade ? "custom" : "predefined"}
                                    onChange={e => {
                                        setIsCustomHeading({ ...isCustomHeading, executive_trade: e.target.value === "custom" });
                                        if (e.target.value === "custom") {
                                            handleBsiChange("executive_summary", null, defaultCustomHeading, "trade", "custom_heading_props", type);
                                        } else {
                                            handleBsiChange(
                                                "executive_summary",
                                                null,
                                                { heading_style: "Heading 3" },
                                                "trade",
                                                "structured_heading",
                                                type
                                            );
                                        }
                                    }}
                                >
                                    <option value="custom">Custom Heading</option>
                                    <option value="predefined">Predefined Heading</option>
                                </select>
                            </div>
                        </div>

                        {isCustomHeading?.executive_trade ? (
                            <>
                                <div className="font-family font-st col-md-3">
                                    <label>Trade Heading Font Name </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.font_id || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "executive_summary",
                                                    "font_id",
                                                    e.target?.value,
                                                    "trade",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {FONT_FAMILY.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="font-size font-st col-md-3">
                                    <label>Trade Heading Font Size</label>

                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.font_size || 0}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.executive_summary?.trade?.custom_heading_props?.font_size;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "font_size",
                                                        values.floatValue,
                                                        "trade",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" Pt"
                                        />
                                    </div>
                                </div>

                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Trade Heading Font Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => handleBsiChange("executive_summary", "color", "", "trade", "custom_heading_props", type)}
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "executive_trade_heading_font_color"
                                                        ? null
                                                        : "executive_trade_heading_font_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.executive_summary?.trade?.custom_heading_props?.color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.executive_summary?.trade?.custom_heading_props?.color
                                                        ? `#${bsiProperty?.executive_summary?.trade?.custom_heading_props?.color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "executive_trade_heading_font_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.executive_summary?.trade?.custom_heading_props?.color === "#000000" ||
                                                        !bsiProperty?.executive_summary?.trade?.custom_heading_props?.color
                                                            ? ""
                                                            : bsiProperty?.executive_summary?.trade?.custom_heading_props?.color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "executive_summary",
                                                            "color",
                                                            color.hex?.replace("#", ""),
                                                            "trade",
                                                            "custom_heading_props",
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Trade Heading Font Bold </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.bold || ""}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "executive_summary",
                                                    "bold",
                                                    e.target?.value === "true",
                                                    "trade",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                        >
                                            <option value={false}>No</option>
                                            <option value={true}>Yes</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Trade Heading Alignment</label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            name="tag"
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.alignment || ""}
                                            onChange={e =>
                                                handleBsiChange(
                                                    "executive_summary",
                                                    "alignment",
                                                    e.target?.value,
                                                    "trade",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                        >
                                            <option value="">Select</option>
                                            <option value={"left"}>Left</option>
                                            <option value={"center"}>Center</option>
                                            <option value={"right"}>Right</option>
                                            <option value={"justify"}>Justify</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Trade Heading Background Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => handleBsiChange("executive_summary", "bg_color", "", "trade", "custom_heading_props", type)}
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "executive_trade_heading_bg_color"
                                                        ? null
                                                        : "executive_trade_heading_bg_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.executive_summary?.trade?.custom_heading_props?.bg_color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.executive_summary?.trade?.custom_heading_props?.bg_color
                                                        ? `#${bsiProperty?.executive_summary?.trade?.custom_heading_props?.bg_color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "executive_trade_heading_bg_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.executive_summary?.trade?.custom_heading_props?.bg_color === "#000000"
                                                            ? ""
                                                            : bsiProperty?.executive_summary?.trade?.custom_heading_props?.bg_color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "executive_summary",
                                                            "bg_color",
                                                            color.hex?.replace("#", ""),
                                                            "trade",
                                                            "custom_heading_props",
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Trade Heading Margin Top</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.top || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.top;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { top: values.floatValue },
                                                        "trade",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Trade Heading Margin Left</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.left || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.left;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { left: values.floatValue },
                                                        "trade",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Trade Heading Margin Right</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.right || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.right;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { right: values.floatValue },
                                                        "trade",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Trade Heading Margin Bottom</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.bottom || ""}
                                            onValueChange={values => {
                                                const currentValue = bsiProperty?.executive_summary?.trade?.custom_heading_props?.cell_margin?.bottom;
                                                if (values.floatValue !== currentValue) {
                                                    handleBsiChange(
                                                        "executive_summary",
                                                        "cell_margin",
                                                        { bottom: values.floatValue },
                                                        "trade",
                                                        "custom_heading_props",
                                                        type
                                                    );
                                                }
                                            }}
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="font-family font-st col-md-3">
                                <label>Trade Heading Style</label>
                                <div className="font-select">
                                    <select
                                        className="dropdown"
                                        value={bsiProperty?.executive_summary?.trade?.structured_heading?.heading_style || "Heading 3"}
                                        onChange={e =>
                                            handleBsiChange("executive_summary", "heading_style", e.target.value, "trade", "structured_heading", type)
                                        }
                                    >
                                        <option value="Heading 1">Heading 1</option>
                                        <option value="Heading 2">Heading 2</option>
                                        <option value="Heading 3">Heading 3</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {type === "bsi" ? "BSI" : "BAI"} Table
                        <button
                            type="button"
                            className={`reset-btn-recom ${
                                hasBandValueChanged(type, ["table_columns", "table_style"], "bbi_table") ? "cursor-disabled" : ""
                            }`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["table_columns", "table_style"], "bbi_table")}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["table_columns", "table_style"],
                                    columnKey: "bbi_table",
                                    band: `${type === "bsi" ? "BSI" : "BAI"}  Table`
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-box mb-0">
                    <div className="styles-sec">
                        <div className="d-flex f-outer">
                            <div className="font-size font-st col-md-3">
                                <label>Table Column</label>

                                <div className={`input-ft-size`}>
                                    <NumberFormat
                                        value={bsiProperty?.bbi_table?.table_columns || 0}
                                        // onValueChange={values => {
                                        //     handleBsiChange("bbi_table", "table_columns", values.floatValue);
                                        // }}
                                        allowNegative={false}
                                        className="cursor-disabled"
                                        disabled
                                        decimalScale={0}
                                    />
                                </div>
                            </div>
                            <div className="font-family font-st col-md-3">
                                <label>Table Font Name </label>
                                <div className={`input-ft-size cursor-diabled`}>
                                    <input className="cursor-diabled" value={bsiProperty?.bbi_table?.table_style || ""} disabled={true} />
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="outer-wrapp-resetprop">
                                <h2 className="recom-h2-sec">
                                    {type === "bsi" ? "BSI" : "BAI"} Table Heading
                                    <button
                                        type="button"
                                        className={`reset-btn-recom ${
                                            hasBandValueChanged(type, ["table_heading"], "bbi_table") ? "cursor-disabled" : ""
                                        }`}
                                        data-tip="Undo changes (made in this edit session) in this Band "
                                        data-for="display_formats"
                                        disabled={hasBandValueChanged(type, ["table_heading"], "bbi_table")}
                                        onClick={() => {
                                            ResetToDefault({
                                                mainKey: type,
                                                subKeys: ["table_heading"],
                                                columnKey: "bbi_table",
                                                band: `${type === "bsi" ? "BSI" : "BAI"}  Table Heading`
                                            });
                                        }}
                                    >
                                        <i className="fas fa-history reset text"></i>
                                    </button>
                                </h2>
                            </div>
                            <div className="styles-sec">
                                <div className="d-flex f-outer">
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Heading Label</label>
                                        <div className={`input-ft-size`}>
                                            <input
                                                value={bsiProperty?.bbi_table?.table_heading?.name || ""}
                                                type="text"
                                                onChange={e => {
                                                    handleBsiChange("bbi_table", "name", e.target?.value, "table_heading", null, type);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Heading Type</label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={isCustomHeading?.table_heading ? "custom" : "predefined"}
                                                onChange={e => {
                                                    setIsCustomHeading({ ...isCustomHeading, table_heading: e.target.value === "custom" });
                                                    if (e.target.value === "custom") {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            null,
                                                            defaultCustomHeading,
                                                            "table_heading",
                                                            "custom_heading_props",
                                                            type
                                                        );
                                                    } else {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            null,
                                                            { heading_style: "Heading 3" },
                                                            "table_heading",
                                                            "structured_heading",
                                                            type
                                                        );
                                                    }
                                                }}
                                            >
                                                <option value="custom">Custom Heading</option>
                                                <option value="predefined">Predefined Heading</option>
                                            </select>
                                        </div>
                                    </div>

                                    {isCustomHeading?.table_heading ? (
                                        <>
                                            <div className="font-family font-st col-md-3">
                                                <label>Table Heading Font Name </label>
                                                <div className="font-select">
                                                    <select
                                                        className="dropdown"
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.font_id || ""}
                                                        onChange={e => {
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "font_id",
                                                                e.target?.value,
                                                                "table_heading",
                                                                "custom_heading_props",
                                                                type
                                                            );
                                                        }}
                                                    >
                                                        <option value="">Select</option>
                                                        {FONT_FAMILY.map(f => (
                                                            <option key={f.id} value={f.id}>
                                                                {f.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="font-size font-st col-md-3">
                                                <label>Table Heading Font Size</label>

                                                <div className={`input-ft-size`}>
                                                    <NumberFormat
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.font_size || 0}
                                                        onValueChange={values => {
                                                            const currentValue =
                                                                bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.font_size;
                                                            if (values.floatValue !== currentValue) {
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "font_size",
                                                                    values.floatValue,
                                                                    "table_heading",
                                                                    "custom_heading_props",
                                                                    type
                                                                );
                                                            }
                                                        }}
                                                        allowNegative={false}
                                                        suffix=" Pt"
                                                    />
                                                </div>
                                            </div>

                                            <div className="font-color font-st col-md-3">
                                                <div className="head_sub">
                                                    <label>Table Heading Font Color </label>
                                                </div>
                                                <div className="dropdown">
                                                    <span
                                                        className="arrow_close"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={e =>
                                                            handleBsiChange("bbi_table", "color", "", "table_heading", "custom_heading_props", type)
                                                        }
                                                    >
                                                        <i class="fas fa-times "></i>
                                                    </span>
                                                    <div
                                                        className="dropdown-toggle"
                                                        type="button"
                                                        onClick={() =>
                                                            setColorModalState(prevColorModalState =>
                                                                prevColorModalState === "custom_table_heading_font_color"
                                                                    ? null
                                                                    : "custom_table_heading_font_color"
                                                            )
                                                        }
                                                    >
                                                        <span>{bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.color || "Select"}</span>
                                                        <span
                                                            className="color-bx"
                                                            style={{
                                                                background: bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.color
                                                                    ? `#${bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.color}`
                                                                    : "#fff"
                                                            }}
                                                        ></span>
                                                        <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                                    </div>
                                                    {colorModalState === "custom_table_heading_font_color" && (
                                                        <div style={styles.popover}>
                                                            <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                            <SketchPicker
                                                                position="right"
                                                                color={
                                                                    bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.color === "#000000"
                                                                        ? ""
                                                                        : bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.color
                                                                }
                                                                presetColors={[
                                                                    "#95cd50",
                                                                    "#ffe242",
                                                                    "#ffa105",
                                                                    "#ff0305",
                                                                    "#0018A8",
                                                                    "#800080",
                                                                    "#3E8EDE",
                                                                    "#417505"
                                                                ]}
                                                                onChange={color =>
                                                                    handleBsiChange(
                                                                        "bbi_table",
                                                                        "color",
                                                                        color.hex?.replace("#", ""),
                                                                        "table_heading",
                                                                        "custom_heading_props"
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="font-family font-st col-md-3">
                                                <label>Table Heading Font Bold </label>
                                                <div className="font-select">
                                                    <select
                                                        className="dropdown"
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.bold || ""}
                                                        onChange={e => {
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "bold",
                                                                e.target?.value === "true",
                                                                "table_heading",
                                                                "custom_heading_props",
                                                                type
                                                            );
                                                        }}
                                                    >
                                                        <option value={false}>No</option>
                                                        <option value={true}>Yes</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="font-family font-st col-md-3">
                                                <label>Table Heading Alignment</label>
                                                <div className="font-select">
                                                    <select
                                                        className="dropdown"
                                                        name="tag"
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.alignment || ""}
                                                        onChange={e =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "alignment",
                                                                e.target?.value,
                                                                "table_heading",
                                                                "custom_heading_props",
                                                                type
                                                            )
                                                        }
                                                    >
                                                        <option value="">Select</option>
                                                        <option value={"left"}>Left</option>
                                                        <option value={"center"}>Center</option>
                                                        <option value={"right"}>Right</option>
                                                        <option value={"justify"}>Justify</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="font-color font-st col-md-3">
                                                <div className="head_sub">
                                                    <label>Table Heading Background Color </label>
                                                </div>
                                                <div className="dropdown">
                                                    <span
                                                        className="arrow_close"
                                                        style={{ cursor: "pointer" }}
                                                        onClick={e =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "bg_color",
                                                                "",
                                                                "table_heading",
                                                                "custom_heading_props",
                                                                type
                                                            )
                                                        }
                                                    >
                                                        <i class="fas fa-times "></i>
                                                    </span>
                                                    <div
                                                        className="dropdown-toggle"
                                                        type="button"
                                                        onClick={() =>
                                                            setColorModalState(prevColorModalState =>
                                                                prevColorModalState === "custom_table_heading_bg_color"
                                                                    ? null
                                                                    : "custom_table_heading_bg_color"
                                                            )
                                                        }
                                                    >
                                                        <span>
                                                            {bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.bg_color || "Select"}
                                                        </span>
                                                        <span
                                                            className="color-bx"
                                                            style={{
                                                                background: bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.bg_color
                                                                    ? `#${bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.bg_color}`
                                                                    : "#fff"
                                                            }}
                                                        ></span>
                                                        <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                                    </div>
                                                    {colorModalState === "custom_table_heading_bg_color" && (
                                                        <div style={styles.popover}>
                                                            <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                            <SketchPicker
                                                                position="right"
                                                                color={
                                                                    bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.bg_color ===
                                                                    "#000000"
                                                                        ? ""
                                                                        : bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.bg_color || ""
                                                                }
                                                                presetColors={[
                                                                    "#95cd50",
                                                                    "#ffe242",
                                                                    "#ffa105",
                                                                    "#ff0305",
                                                                    "#0018A8",
                                                                    "#800080",
                                                                    "#3E8EDE",
                                                                    "#417505"
                                                                ]}
                                                                onChange={color =>
                                                                    handleBsiChange(
                                                                        "bbi_table",
                                                                        "bg_color",
                                                                        color.hex?.replace("#", ""),
                                                                        "table_heading",
                                                                        "custom_heading_props",
                                                                        type
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            <div className="font-size font-st col-md-3">
                                                <label>Table Heading Cell Margin Top</label>
                                                <div className={`input-ft-size`}>
                                                    <NumberFormat
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.top || ""}
                                                        onValueChange={values => {
                                                            const currentValue =
                                                                bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.top;
                                                            if (values.floatValue !== currentValue) {
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "cell_margin",
                                                                    { top: values.floatValue },
                                                                    "table_heading",
                                                                    "custom_heading_props",
                                                                    type
                                                                );
                                                            }
                                                        }}
                                                        allowNegative={false}
                                                        suffix=" inch"
                                                    />
                                                </div>
                                            </div>
                                            <div className="font-size font-st col-md-3">
                                                <label>Table Heading Cell Margin Left</label>
                                                <div className={`input-ft-size`}>
                                                    <NumberFormat
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.left || ""}
                                                        onValueChange={values => {
                                                            const currentValue =
                                                                bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.left;
                                                            if (values.floatValue !== currentValue) {
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "cell_margin",
                                                                    { left: values.floatValue },
                                                                    "table_heading",
                                                                    "custom_heading_props",
                                                                    type
                                                                );
                                                            }
                                                        }}
                                                        allowNegative={false}
                                                        suffix=" inch"
                                                    />
                                                </div>
                                            </div>
                                            <div className="font-size font-st col-md-3">
                                                <label>Table Heading Cell Margin Right</label>
                                                <div className={`input-ft-size`}>
                                                    <NumberFormat
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.right || ""}
                                                        onValueChange={values => {
                                                            const currentValue =
                                                                bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.right;
                                                            if (values.floatValue !== currentValue) {
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "cell_margin",
                                                                    { right: values.floatValue },
                                                                    "table_heading",
                                                                    "custom_heading_props",
                                                                    type
                                                                );
                                                            }
                                                        }}
                                                        allowNegative={false}
                                                        suffix=" inch"
                                                    />
                                                </div>
                                            </div>
                                            <div className="font-size font-st col-md-3">
                                                <label>Table Heading Cell Margin Bottom</label>
                                                <div className={`input-ft-size`}>
                                                    <NumberFormat
                                                        value={bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.bottom || ""}
                                                        onValueChange={values => {
                                                            const currentValue =
                                                                bsiProperty?.bbi_table?.table_heading?.custom_heading_props?.cell_margin?.bottom;
                                                            if (values.floatValue !== currentValue) {
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "cell_margin",
                                                                    { bottom: values.floatValue },
                                                                    "table_heading",
                                                                    "custom_heading_props",
                                                                    type
                                                                );
                                                            }
                                                        }}
                                                        allowNegative={false}
                                                        suffix=" inch"
                                                    />
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="font-family font-st col-md-3">
                                            <label>Table Heading Style</label>
                                            <div className="font-select">
                                                <select
                                                    className="dropdown"
                                                    value={bsiProperty?.bbi_table?.table_heading?.structured_heading?.heading_style || ""}
                                                    onChange={e =>
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "heading_style",
                                                            e.target.value,
                                                            "table_heading",
                                                            "structured_heading",
                                                            type
                                                        )
                                                    }
                                                >
                                                    <option value="Heading 1">Heading 1</option>
                                                    <option value="Heading 2">Heading 2</option>
                                                    <option value="Heading 3">Heading 3</option>
                                                </select>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="outer-wrapp-resetprop">
                                <h2 className="recom-h2-sec">
                                    {type === "bsi" ? "BSI Site" : "BAI Addition"} Table Heading
                                    <button
                                        type="button"
                                        className={`reset-btn-recom ${
                                            hasBandValueChanged(type, ["building_fields_heading"], "bbi_table") ? "cursor-disabled" : ""
                                        }`}
                                        data-tip="Undo changes (made in this edit session) in this Band "
                                        data-for="display_formats"
                                        disabled={hasBandValueChanged(type, ["building_fields_heading"], "bbi_table")}
                                        onClick={() => {
                                            ResetToDefault({
                                                mainKey: type,
                                                subKeys: ["building_fields_heading"],
                                                columnKey: "bbi_table",
                                                band: `${type === "bsi" ? "BSI Site" : "BAI Addition"} Table Heading`
                                            });
                                        }}
                                    >
                                        <i className="fas fa-history reset text"></i>
                                    </button>
                                </h2>
                            </div>
                            <div className="styles-sec">
                                <div className="d-flex f-outer">
                                    <div className="font-family font-st col-md-3">
                                        <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Font Name </label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={bsiProperty?.bbi_table?.building_fields_heading?.font_id || ""}
                                                onChange={e => {
                                                    handleBsiChange("bbi_table", "font_id", e.target?.value, "building_fields_heading", null, type);
                                                }}
                                            >
                                                <option value="">Select</option>
                                                {FONT_FAMILY.map(f => (
                                                    <option key={f.id} value={f.id}>
                                                        {f.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-size font-st col-md-3">
                                        <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Font Size</label>

                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.building_fields_heading?.font_size || ""}
                                                onValueChange={e => {
                                                    {
                                                        const currentValue = bsiProperty?.bbi_table?.building_fields_heading?.font_size;
                                                        if (e.floatValue !== currentValue) {
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "font_size",
                                                                e.floatValue,
                                                                "building_fields_heading",
                                                                null,
                                                                type
                                                            );
                                                        }
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>

                                    <div className="font-color font-st col-md-3">
                                        <div className="head_sub">
                                            <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Font Color </label>
                                        </div>
                                        <div className="dropdown sketch-wide">
                                            <span
                                                className="arrow_close"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => handleBsiChange("bbi_table", "color", "", "building_fields_heading", null, type)}
                                            >
                                                <i class="fas fa-times "></i>
                                            </span>
                                            <div
                                                className="dropdown-toggle"
                                                type="button"
                                                onClick={() =>
                                                    setColorModalState(prevColorModalState =>
                                                        prevColorModalState === "building_fields_heading_color"
                                                            ? null
                                                            : "building_fields_heading_color"
                                                    )
                                                }
                                            >
                                                <span>{bsiProperty?.bbi_table?.building_fields_heading?.color || "Select"}</span>
                                                <span
                                                    className="color-bx"
                                                    style={{
                                                        background: bsiProperty?.bbi_table?.building_fields_heading?.color
                                                            ? `#${bsiProperty?.bbi_table?.building_fields_heading?.color}`
                                                            : "#fff"
                                                    }}
                                                ></span>
                                                <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                            </div>
                                            {colorModalState === "building_fields_heading_color" && (
                                                <div style={styles.popover}>
                                                    <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                    <SketchPicker
                                                        position="right"
                                                        color={
                                                            bsiProperty?.bbi_table?.building_fields_heading?.color === "#000000" ||
                                                            !bsiProperty?.bbi_table?.building_fields_heading?.color
                                                                ? ""
                                                                : bsiProperty?.bbi_table?.building_fields_heading?.color
                                                        }
                                                        presetColors={[
                                                            "#95cd50",
                                                            "#ffe242",
                                                            "#ffa105",
                                                            "#ff0305",
                                                            "#0018A8",
                                                            "#800080",
                                                            "#3E8EDE",
                                                            "#417505"
                                                        ]}
                                                        onChange={color =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "color",
                                                                color.hex?.replace("#", ""),
                                                                "building_fields_heading",
                                                                null,
                                                                type
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Font Bold </label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={bsiProperty?.bbi_table?.building_fields_heading?.bold || ""}
                                                onChange={e => {
                                                    handleBsiChange(
                                                        "bbi_table",
                                                        "bold",
                                                        e.target?.value === "true",
                                                        "building_fields_heading",
                                                        null,
                                                        type
                                                    );
                                                }}
                                            >
                                                <option value={false}>No</option>
                                                <option value={true}>Yes</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Alignment</label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                name="tag"
                                                value={bsiProperty?.bbi_table?.building_fields_heading?.alignment || ""}
                                                onChange={e =>
                                                    handleBsiChange("bbi_table", "alignment", e.target?.value, "building_fields_heading", null, type)
                                                }
                                            >
                                                <option value="">Select</option>
                                                <option value={"left"}>Left</option>
                                                <option value={"center"}>Center</option>
                                                <option value={"right"}>Right</option>
                                                <option value={"justify"}>Justify</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-color font-st col-md-3">
                                        <div className="head_sub">
                                            <label>{type === "bsi" ? "Site" : "Addition"} Fields Background Color </label>
                                        </div>
                                        <div className="dropdown sketch-wide">
                                            <span
                                                className="arrow_close"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => handleBsiChange("bbi_table", "bg_color", "", "building_fields_heading", null, type)}
                                            >
                                                <i class="fas fa-times "></i>
                                            </span>
                                            <div
                                                className="dropdown-toggle"
                                                type="button"
                                                onClick={() =>
                                                    setColorModalState(prevColorModalState =>
                                                        prevColorModalState === "building_fields_heading_bg_color"
                                                            ? null
                                                            : "building_fields_heading_bg_color"
                                                    )
                                                }
                                            >
                                                <span>{bsiProperty?.bbi_table?.building_fields_heading?.bg_color || "Select"}</span>
                                                <span
                                                    className="color-bx"
                                                    style={{
                                                        background: bsiProperty?.bbi_table?.building_fields_heading?.bg_color
                                                            ? `#${bsiProperty?.bbi_table?.building_fields_heading?.bg_color}`
                                                            : "#fff"
                                                    }}
                                                ></span>
                                                <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                            </div>
                                            {colorModalState === "building_fields_heading_bg_color" && (
                                                <div style={styles.popover}>
                                                    <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                    <SketchPicker
                                                        position="right"
                                                        color={
                                                            bsiProperty?.bbi_table?.building_fields_heading?.bg_color === "#000000" ||
                                                            !bsiProperty?.bbi_table?.building_fields_heading?.bg_color
                                                                ? ""
                                                                : bsiProperty?.bbi_table?.building_fields_heading?.bg_color
                                                        }
                                                        presetColors={[
                                                            "#95cd50",
                                                            "#ffe242",
                                                            "#ffa105",
                                                            "#ff0305",
                                                            "#0018A8",
                                                            "#800080",
                                                            "#3E8EDE",
                                                            "#417505"
                                                        ]}
                                                        onChange={color =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "bg_color",
                                                                color.hex?.replace("#", ""),
                                                                "building_fields_heading",
                                                                null,
                                                                type
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="font-family font-st col-md-3">
                                        <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Space Before </label>
                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.building_fields_heading?.space_before || 0}
                                                onValueChange={values => {
                                                    const currentValue = bsiProperty?.bbi_table?.building_fields_heading?.space_before;
                                                    if (values.floatValue !== currentValue) {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "space_before",
                                                            values.floatValue,
                                                            "building_fields_heading",
                                                            null,
                                                            type
                                                        );
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>{type === "bsi" ? "Site" : "Addition"} Table Heading Space After </label>
                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.building_fields_heading?.space_after || 0}
                                                onValueChange={values => {
                                                    const currentValue = bsiProperty?.bbi_table?.building_fields_heading?.space_after;
                                                    if (values.floatValue !== currentValue) {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "space_after",
                                                            values.floatValue,
                                                            "building_fields_heading",
                                                            null,
                                                            type
                                                        );
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {type === "bsi" ? (
                            <div>
                                <div className="outer-wrapp-resetprop">
                                    <h2 className="recom-h2-sec">
                                        {type === "bsi" ? "BSI" : "BAI"} Building Fields Heading
                                        <button
                                            type="button"
                                            className={`reset-btn-recom ${
                                                hasBandValueChanged(type, ["addition_fields_heading"], "bbi_table") ? "cursor-disabled" : ""
                                            }`}
                                            data-tip="Undo changes (made in this edit session) in this Band "
                                            data-for="display_formats"
                                            disabled={hasBandValueChanged(type, ["addition_fields_heading"], "bbi_table")}
                                            onClick={() => {
                                                ResetToDefault({
                                                    mainKey: type,
                                                    subKeys: ["addition_fields_heading"],
                                                    columnKey: "bbi_table",
                                                    band: `${type === "bsi" ? "BSI" : "BAI"}  Building Fields Heading`
                                                });
                                            }}
                                        >
                                            <i className="fas fa-history reset text"></i>
                                        </button>
                                    </h2>
                                </div>
                                <div className="styles-sec">
                                    <div className="d-flex f-outer">
                                        <div className="font-family font-st col-md-3">
                                            <label>Building Fields Heading Font Name </label>
                                            <div className="font-select">
                                                <select
                                                    className="dropdown"
                                                    value={bsiProperty?.bbi_table?.addition_fields_heading?.font_id || ""}
                                                    onChange={e => {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "font_id",
                                                            e.target?.value,
                                                            "addition_fields_heading",
                                                            null,
                                                            type
                                                        );
                                                    }}
                                                >
                                                    <option value="">Select</option>
                                                    {FONT_FAMILY.map(f => (
                                                        <option key={f.id} value={f.id}>
                                                            {f.name}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="font-size font-st col-md-3">
                                            <label>Building Fields Heading Font Size</label>

                                            <div className={`input-ft-size`}>
                                                <NumberFormat
                                                    value={bsiProperty?.bbi_table?.addition_fields_heading?.font_size || ""}
                                                    onValueChange={e => {
                                                        const currentValue = bsiProperty?.bbi_table?.addition_fields_heading?.font_size;
                                                        if (e.floatValue !== currentValue) {
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "font_size",
                                                                e.floatValue,
                                                                "addition_fields_heading",
                                                                null,
                                                                type
                                                            );
                                                        }
                                                    }}
                                                    allowNegative={false}
                                                    suffix=" Pt"
                                                />
                                            </div>
                                        </div>

                                        <div className="font-color font-st col-md-3">
                                            <div className="head_sub">
                                                <label>Building Fields Heading Font Color </label>
                                            </div>
                                            <div className="dropdown sketch-wide">
                                                <span
                                                    className="arrow_close"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={e => handleBsiChange("bbi_table", "color", "", "addition_fields_heading", null, type)}
                                                >
                                                    <i class="fas fa-times "></i>
                                                </span>
                                                <div
                                                    className="dropdown-toggle"
                                                    type="button"
                                                    onClick={() =>
                                                        setColorModalState(prevColorModalState =>
                                                            prevColorModalState === "addition_fields_heading_color"
                                                                ? null
                                                                : "addition_fields_heading_color"
                                                        )
                                                    }
                                                >
                                                    <span>{bsiProperty?.bbi_table?.addition_fields_heading?.color || "Select"}</span>
                                                    <span
                                                        className="color-bx"
                                                        style={{
                                                            background: bsiProperty?.bbi_table?.addition_fields_heading?.color
                                                                ? `#${bsiProperty?.bbi_table?.addition_fields_heading?.color}`
                                                                : "#fff"
                                                        }}
                                                    ></span>
                                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                                </div>
                                                {colorModalState === "addition_fields_heading_color" && (
                                                    <div style={styles.popover}>
                                                        <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                        <SketchPicker
                                                            position="right"
                                                            color={
                                                                bsiProperty?.bbi_table?.addition_fields_heading?.color === "#000000" ||
                                                                !bsiProperty?.bbi_table?.addition_fields_heading?.color
                                                                    ? ""
                                                                    : bsiProperty?.bbi_table?.addition_fields_heading?.color
                                                            }
                                                            presetColors={[
                                                                "#95cd50",
                                                                "#ffe242",
                                                                "#ffa105",
                                                                "#ff0305",
                                                                "#0018A8",
                                                                "#800080",
                                                                "#3E8EDE",
                                                                "#417505"
                                                            ]}
                                                            onChange={color =>
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "color",
                                                                    color.hex?.replace("#", ""),
                                                                    "addition_fields_heading",
                                                                    null,
                                                                    type
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="font-family font-st col-md-3">
                                            <label>Building Fields Heading Font Bold </label>
                                            <div className="font-select">
                                                <select
                                                    className="dropdown"
                                                    value={bsiProperty?.bbi_table?.addition_fields_heading?.bold || ""}
                                                    onChange={e => {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "bold",
                                                            e.target?.value === "true",
                                                            "addition_fields_heading",
                                                            null,
                                                            type
                                                        );
                                                    }}
                                                >
                                                    <option value={false}>No</option>
                                                    <option value={true}>Yes</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="font-family font-st col-md-3">
                                            <label>Building Fields Heading Alignment</label>
                                            <div className="font-select">
                                                <select
                                                    className="dropdown"
                                                    name="tag"
                                                    value={bsiProperty?.bbi_table?.addition_fields_heading?.alignment || ""}
                                                    onChange={e =>
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "alignment",
                                                            e.target?.value,
                                                            "addition_fields_heading",
                                                            null,
                                                            type
                                                        )
                                                    }
                                                >
                                                    <option value="">Select</option>
                                                    <option value={"left"}>Left</option>
                                                    <option value={"center"}>Center</option>
                                                    <option value={"right"}>Right</option>
                                                    <option value={"justify"}>Justify</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="font-color font-st col-md-3">
                                            <div className="head_sub">
                                                <label>Building Fields Background Color </label>
                                            </div>
                                            <div className="dropdown sketch-wide">
                                                <span
                                                    className="arrow_close"
                                                    style={{ cursor: "pointer" }}
                                                    onClick={e => handleBsiChange("bbi_table", "bg_color", "", "addition_fields_heading", null, type)}
                                                >
                                                    <i class="fas fa-times "></i>
                                                </span>
                                                <div
                                                    className="dropdown-toggle"
                                                    type="button"
                                                    onClick={() =>
                                                        setColorModalState(prevColorModalState =>
                                                            prevColorModalState === "addition_fields_heading_bg_color"
                                                                ? null
                                                                : "addition_fields_heading_bg_color"
                                                        )
                                                    }
                                                >
                                                    <span>{bsiProperty?.bbi_table?.addition_fields_heading?.bg_color || "Select"}</span>
                                                    <span
                                                        className="color-bx"
                                                        style={{
                                                            background: bsiProperty?.bbi_table?.addition_fields_heading?.bg_color
                                                                ? `#${bsiProperty?.bbi_table?.addition_fields_heading?.bg_color}`
                                                                : "#fff"
                                                        }}
                                                    ></span>
                                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                                </div>
                                                {colorModalState === "addition_fields_heading_bg_color" && (
                                                    <div style={styles.popover}>
                                                        <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                        <SketchPicker
                                                            position="right"
                                                            color={
                                                                bsiProperty?.bbi_table?.addition_fields_heading?.bg_color === "#000000" ||
                                                                !bsiProperty?.bbi_table?.addition_fields_heading?.bg_color
                                                                    ? ""
                                                                    : bsiProperty?.bbi_table?.addition_fields_heading?.bg_color
                                                            }
                                                            presetColors={[
                                                                "#95cd50",
                                                                "#ffe242",
                                                                "#ffa105",
                                                                "#ff0305",
                                                                "#0018A8",
                                                                "#800080",
                                                                "#3E8EDE",
                                                                "#417505"
                                                            ]}
                                                            onChange={color =>
                                                                handleBsiChange(
                                                                    "bbi_table",
                                                                    "bg_color",
                                                                    color.hex?.replace("#", ""),
                                                                    "addition_fields_heading",
                                                                    null,
                                                                    type
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className="font-family font-st col-md-3">
                                            <label>Building Fields Heading Space Before </label>
                                            <div className={`input-ft-size`}>
                                                <NumberFormat
                                                    value={bsiProperty?.bbi_table?.addition_fields_heading?.space_before || 0}
                                                    onValueChange={values => {
                                                        const currentValue = bsiProperty?.bbi_table?.addition_fields_heading?.space_before;
                                                        if (values.floatValue !== currentValue) {
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "space_before",
                                                                values.floatValue,
                                                                "addition_fields_heading",
                                                                null,
                                                                type
                                                            );
                                                        }
                                                    }}
                                                    allowNegative={false}
                                                    suffix=" Pt"
                                                />
                                            </div>
                                        </div>
                                        <div className="font-family font-st col-md-3">
                                            <label>Building Fields Heading Space After </label>
                                            <div className={`input-ft-size`}>
                                                <NumberFormat
                                                    value={bsiProperty?.bbi_table?.addition_fields_heading?.space_after || 0}
                                                    onValueChange={values => {
                                                        const currentValue = bsiProperty?.bbi_table?.addition_fields_heading?.space_after;
                                                        if (values.floatValue !== currentValue) {
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "space_after",
                                                                values.floatValue,
                                                                "addition_fields_heading",
                                                                null,
                                                                type
                                                            );
                                                        }
                                                    }}
                                                    allowNegative={false}
                                                    suffix=" Pt"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}

                        <div>
                            <div className="outer-wrapp-resetprop">
                                <h2 className="recom-h2-sec">
                                    {type === "bsi" ? "BSI" : "BAI"} Table Fields
                                    <button
                                        type="button"
                                        className={`reset-btn-recom ${
                                            hasBandValueChanged(type, ["table_fields"], "bbi_table") ? "cursor-disabled" : ""
                                        }`}
                                        data-tip="Undo changes (made in this edit session) in this Band "
                                        data-for="display_formats"
                                        disabled={hasBandValueChanged(type, ["table_fields"], "bbi_table")}
                                        onClick={() => {
                                            ResetToDefault({
                                                mainKey: type,
                                                subKeys: ["table_fields"],
                                                columnKey: "bbi_table",
                                                band: `${type === "bsi" ? "BSI" : "BAI"}  Table Fields`
                                            });
                                        }}
                                    >
                                        <i className="fas fa-history reset text"></i>
                                    </button>
                                </h2>
                            </div>
                            <div className="styles-sec">
                                <div className="d-flex f-outer">
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Font Name </label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={bsiProperty?.bbi_table?.table_fields?.font_id || ""}
                                                onChange={e => {
                                                    handleBsiChange("bbi_table", "font_id", e.target?.value, "table_fields", null, type);
                                                }}
                                            >
                                                <option value="">Select</option>
                                                {FONT_FAMILY.map(f => (
                                                    <option key={f.id} value={f.id}>
                                                        {f.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-size font-st col-md-3">
                                        <label>Table Fields Font Size</label>

                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.table_fields?.font_size || ""}
                                                onValueChange={e => {
                                                    const currentValue = bsiProperty?.bbi_table?.table_fields?.font_size;
                                                    if (e.floatValue !== currentValue) {
                                                        handleBsiChange("bbi_table", "font_size", e.floatValue, "table_fields", null, type);
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>

                                    <div className="font-color font-st col-md-3">
                                        <div className="head_sub">
                                            <label>Table Fields Font Color </label>
                                        </div>
                                        <div className="dropdown sketch-wide">
                                            <span
                                                className="arrow_close"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => handleBsiChange("bbi_table", "color", "", "table_fields", null, type)}
                                            >
                                                <i class="fas fa-times "></i>
                                            </span>
                                            <div
                                                className="dropdown-toggle"
                                                type="button"
                                                onClick={() =>
                                                    setColorModalState(prevColorModalState =>
                                                        prevColorModalState === "table_fields_color" ? null : "table_fields_color"
                                                    )
                                                }
                                            >
                                                <span>{bsiProperty?.bbi_table?.table_fields?.color || "Select"}</span>
                                                <span
                                                    className="color-bx"
                                                    style={{
                                                        background: bsiProperty?.bbi_table?.table_fields?.color
                                                            ? `#${bsiProperty?.bbi_table?.table_fields?.color}`
                                                            : "#fff"
                                                    }}
                                                ></span>
                                                <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                            </div>
                                            {colorModalState === "table_fields_color" && (
                                                <div style={styles.popover}>
                                                    <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                    <SketchPicker
                                                        position="right"
                                                        color={
                                                            bsiProperty?.bbi_table?.table_fields?.color === "#000000" ||
                                                            !bsiProperty?.bbi_table?.table_fields?.color
                                                                ? ""
                                                                : bsiProperty?.bbi_table?.table_fields?.color
                                                        }
                                                        presetColors={[
                                                            "#95cd50",
                                                            "#ffe242",
                                                            "#ffa105",
                                                            "#ff0305",
                                                            "#0018A8",
                                                            "#800080",
                                                            "#3E8EDE",
                                                            "#417505"
                                                        ]}
                                                        onChange={color =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "color",
                                                                color.hex?.replace("#", ""),
                                                                "table_fields",
                                                                null,
                                                                type
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Font Bold </label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={bsiProperty?.bbi_table?.table_fields?.bold || ""}
                                                onChange={e => {
                                                    handleBsiChange("bbi_table", "bold", e.target?.value === "true", "table_fields", null, type);
                                                }}
                                            >
                                                <option value={false}>No</option>
                                                <option value={true}>Yes</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Alignment</label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                name="tag"
                                                value={bsiProperty?.bbi_table?.table_fields?.alignment || ""}
                                                onChange={e => handleBsiChange("bbi_table", "alignment", e.target?.value, "table_fields", null, type)}
                                            >
                                                <option value="">Select</option>
                                                <option value={"left"}>Left</option>
                                                <option value={"center"}>Center</option>
                                                <option value={"right"}>Right</option>
                                                <option value={"justify"}>Justify</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-color font-st col-md-3">
                                        <div className="head_sub">
                                            <label>Table Fields Background Color </label>
                                        </div>
                                        <div className="dropdown sketch-wide">
                                            <span
                                                className="arrow_close"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => handleBsiChange("bbi_table", "bg_color", "", "table_fields", null, type)}
                                            >
                                                <i class="fas fa-times "></i>
                                            </span>
                                            <div
                                                className="dropdown-toggle"
                                                type="button"
                                                onClick={() =>
                                                    setColorModalState(prevColorModalState =>
                                                        prevColorModalState === "table_fields_bg_color" ? null : "table_fields_bg_color"
                                                    )
                                                }
                                            >
                                                <span>{bsiProperty?.bbi_table?.table_fields?.bg_color || "Select"}</span>
                                                <span
                                                    className="color-bx"
                                                    style={{
                                                        background: bsiProperty?.bbi_table?.table_fields?.bg_color
                                                            ? `#${bsiProperty?.bbi_table?.table_fields?.bg_color}`
                                                            : "#fff"
                                                    }}
                                                ></span>
                                                <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                            </div>
                                            {colorModalState === "table_fields_bg_color" && (
                                                <div style={styles.popover}>
                                                    <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                    <SketchPicker
                                                        position="right"
                                                        color={
                                                            bsiProperty?.bbi_table?.table_fields?.bg_color === "#000000" ||
                                                            !bsiProperty?.bbi_table?.table_fields?.bg_color
                                                                ? ""
                                                                : bsiProperty?.bbi_table?.table_fields?.bg_color
                                                        }
                                                        presetColors={[
                                                            "#95cd50",
                                                            "#ffe242",
                                                            "#ffa105",
                                                            "#ff0305",
                                                            "#0018A8",
                                                            "#800080",
                                                            "#3E8EDE",
                                                            "#417505"
                                                        ]}
                                                        onChange={color =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "bg_color",
                                                                color.hex?.replace("#", ""),
                                                                "table_fields",
                                                                null,
                                                                type
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Space Before </label>
                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.table_fields?.space_before || 0}
                                                onValueChange={values => {
                                                    const currentValue = bsiProperty?.bbi_table?.table_fields?.space_before;
                                                    if (values.floatValue !== currentValue) {
                                                        handleBsiChange("bbi_table", "space_before", values.floatValue, "table_fields", null, type);
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Space After </label>
                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.table_fields?.space_after || 0}
                                                onValueChange={values => {
                                                    const currentValue = bsiProperty?.bbi_table?.table_fields?.space_after;
                                                    if (values.floatValue !== currentValue) {
                                                        handleBsiChange("bbi_table", "space_after", values.floatValue, "table_fields", null, type);
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="outer-wrapp-resetprop">
                                <h2 className="recom-h2-sec">
                                    {type === "bsi" ? "BSI" : "BAI"} Table Fields Value
                                    <button
                                        type="button"
                                        className={`reset-btn-recom ${
                                            hasBandValueChanged(type, ["table_field_value"], "bbi_table") ? "cursor-disabled" : ""
                                        }`}
                                        data-tip="Undo changes (made in this edit session) in this Band "
                                        data-for="display_formats"
                                        disabled={hasBandValueChanged(type, ["table_field_value"], "bbi_table")}
                                        onClick={() => {
                                            ResetToDefault({
                                                mainKey: type,
                                                subKeys: ["table_field_value"],
                                                columnKey: "bbi_table",
                                                band: `${type === "bsi" ? "BSI" : "BAI"}  Table Fields Value`
                                            });
                                        }}
                                    >
                                        <i className="fas fa-history reset text"></i>
                                    </button>
                                </h2>
                            </div>
                            <div className="styles-sec">
                                <div className="d-flex f-outer">
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Value Font Name </label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={bsiProperty?.bbi_table?.table_field_value?.font_id || ""}
                                                onChange={e => {
                                                    handleBsiChange("bbi_table", "font_id", e.target?.value, "table_field_value", null, type);
                                                }}
                                            >
                                                <option value="">Select</option>
                                                {FONT_FAMILY.map(f => (
                                                    <option key={f.id} value={f.id}>
                                                        {f.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-size font-st col-md-3">
                                        <label>Table Fields Value Font Size</label>

                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.table_field_value?.font_size || ""}
                                                onValueChange={e => {
                                                    const currentValue = bsiProperty?.bbi_table?.table_field_value?.font_size;
                                                    if (e.floatValue !== currentValue) {
                                                        handleBsiChange("bbi_table", "font_size", e.floatValue, "table_field_value", null, type);
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>

                                    <div className="font-color font-st col-md-3">
                                        <div className="head_sub">
                                            <label>Table Fields Value Font Color </label>
                                        </div>
                                        <div className="dropdown sketch-wide">
                                            <span
                                                className="arrow_close"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => handleBsiChange("bbi_table", "color", "", "table_field_value", null, type)}
                                            >
                                                <i class="fas fa-times "></i>
                                            </span>
                                            <div
                                                className="dropdown-toggle"
                                                type="button"
                                                onClick={() =>
                                                    setColorModalState(prevColorModalState =>
                                                        prevColorModalState === "table_field_value_color" ? null : "table_field_value_color"
                                                    )
                                                }
                                            >
                                                <span>{bsiProperty?.bbi_table?.table_field_value?.color || "Select"}</span>
                                                <span
                                                    className="color-bx"
                                                    style={{
                                                        background: bsiProperty?.bbi_table?.table_field_value?.color
                                                            ? `#${bsiProperty?.bbi_table?.table_field_value?.color}`
                                                            : "#fff"
                                                    }}
                                                ></span>
                                                <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                            </div>
                                            {colorModalState === "table_field_value_color" && (
                                                <div style={styles.popover}>
                                                    <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                    <SketchPicker
                                                        position="right"
                                                        color={
                                                            bsiProperty?.bbi_table?.table_field_value?.color === "#000000" ||
                                                            !bsiProperty?.bbi_table?.table_field_value?.color
                                                                ? ""
                                                                : bsiProperty?.bbi_table?.table_field_value?.color
                                                        }
                                                        presetColors={[
                                                            "#95cd50",
                                                            "#ffe242",
                                                            "#ffa105",
                                                            "#ff0305",
                                                            "#0018A8",
                                                            "#800080",
                                                            "#3E8EDE",
                                                            "#417505"
                                                        ]}
                                                        onChange={color =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "color",
                                                                color.hex?.replace("#", ""),
                                                                "table_field_value",
                                                                null,
                                                                type
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Value Font Bold </label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                value={bsiProperty?.bbi_table?.table_field_value?.bold || ""}
                                                onChange={e => {
                                                    handleBsiChange("bbi_table", "bold", e.target?.value === "true", "table_field_value", null, type);
                                                }}
                                            >
                                                <option value={false}>No</option>
                                                <option value={true}>Yes</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Value Alignment</label>
                                        <div className="font-select">
                                            <select
                                                className="dropdown"
                                                name="tag"
                                                value={bsiProperty?.bbi_table?.table_field_value?.alignment || ""}
                                                onChange={e =>
                                                    handleBsiChange("bbi_table", "alignment", e.target?.value, "table_field_value", null, type)
                                                }
                                            >
                                                <option value="">Select</option>
                                                <option value={"left"}>Left</option>
                                                <option value={"center"}>Center</option>
                                                <option value={"right"}>Right</option>
                                                <option value={"justify"}>Justify</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="font-color font-st col-md-3">
                                        <div className="head_sub">
                                            <label>Table Fields Background Color </label>
                                        </div>
                                        <div className="dropdown sketch-wide">
                                            <span
                                                className="arrow_close"
                                                style={{ cursor: "pointer" }}
                                                onClick={e => handleBsiChange("bbi_table", "bg_color", "", "table_field_value", null, type)}
                                            >
                                                <i class="fas fa-times "></i>
                                            </span>
                                            <div
                                                className="dropdown-toggle"
                                                type="button"
                                                onClick={() =>
                                                    setColorModalState(prevColorModalState =>
                                                        prevColorModalState === "table_field_value_bg_color" ? null : "table_field_value_bg_color"
                                                    )
                                                }
                                            >
                                                <span>{bsiProperty?.bbi_table?.table_field_value?.bg_color || "Select"}</span>
                                                <span
                                                    className="color-bx"
                                                    style={{
                                                        background: bsiProperty?.bbi_table?.table_field_value?.bg_color
                                                            ? `#${bsiProperty?.bbi_table?.table_field_value?.bg_color}`
                                                            : "#fff"
                                                    }}
                                                ></span>
                                                <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                            </div>
                                            {colorModalState === "table_field_value_bg_color" && (
                                                <div style={styles.popover}>
                                                    <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                    <SketchPicker
                                                        position="right"
                                                        color={
                                                            bsiProperty?.bbi_table?.table_field_value?.bg_color === "#000000" ||
                                                            !bsiProperty?.bbi_table?.table_field_value?.bg_color
                                                                ? ""
                                                                : bsiProperty?.bbi_table?.table_field_value?.bg_color
                                                        }
                                                        presetColors={[
                                                            "#95cd50",
                                                            "#ffe242",
                                                            "#ffa105",
                                                            "#ff0305",
                                                            "#0018A8",
                                                            "#800080",
                                                            "#3E8EDE",
                                                            "#417505"
                                                        ]}
                                                        onChange={color =>
                                                            handleBsiChange(
                                                                "bbi_table",
                                                                "bg_color",
                                                                color.hex?.replace("#", ""),
                                                                "table_field_value",
                                                                null,
                                                                type
                                                            )
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Value Space Before </label>
                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.table_field_value?.space_before || 0}
                                                onValueChange={values => {
                                                    const currentValue = bsiProperty?.bbi_table?.table_field_value?.space_before;
                                                    if (values.floatValue !== currentValue) {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "space_before",
                                                            values.floatValue,
                                                            "table_field_value",
                                                            null,
                                                            type
                                                        );
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>
                                    <div className="font-family font-st col-md-3">
                                        <label>Table Fields Value Space After </label>
                                        <div className={`input-ft-size`}>
                                            <NumberFormat
                                                value={bsiProperty?.bbi_table?.table_field_value?.space_after || 0}
                                                onValueChange={values => {
                                                    const currentValue = bsiProperty?.bbi_table?.table_field_value?.space_after;
                                                    if (values.floatValue !== currentValue) {
                                                        handleBsiChange(
                                                            "bbi_table",
                                                            "space_after",
                                                            values.floatValue,
                                                            "table_field_value",
                                                            null,
                                                            type
                                                        );
                                                    }
                                                }}
                                                allowNegative={false}
                                                suffix=" Pt"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="styles-box">
                <h2 className="mt-0">{type === "bsi" ? "BSI" : "BAI"} Table Column Configuration</h2>
                <>
                    <div className="outer-wrapp-resetprop">
                        <h2 className="mt-0 recom-h2-sec">
                            {type === "bsi" ? "Site" : "Addition"} Fields
                            <button
                                type="button"
                                className={`reset-btn-recom ${
                                    hasBandValueChanged(type, ["building_fields"], "bbi_table", "bbi_table_column_configuration", "fields", type)
                                        ? "cursor-disabled"
                                        : ""
                                }`}
                                data-tip="Undo changes (made in this edit session) in this Band "
                                data-for="display_formats"
                                disabled={hasBandValueChanged(
                                    type,
                                    ["building_fields"],
                                    "bbi_table",
                                    "bbi_table_column_configuration",
                                    "fields",
                                    type
                                )}
                                onClick={() => {
                                    ResetToDefault({
                                        mainKey: type,
                                        subKeys: ["building_fields"],
                                        columnKey: "bbi_table",
                                        columnKey2: "bbi_table_column_configuration",
                                        columnKey3: "fields",
                                        band: "Site Fields"
                                    });
                                }}
                            >
                                <i className="fas fa-history reset text"></i>
                            </button>
                        </h2>
                    </div>
                    <button type="button" className="btn btn-primary m-4" onClick={toggleTableConfig}>
                        Config Columns
                    </button>
                </>
                {type === "bsi" ? (
                    <>
                        <div className="outer-wrapp-resetprop">
                            <h2 className="mt-0 recom-h2-sec">
                                Building Fields
                                <button
                                    type="button"
                                    className={`reset-btn-recom ${
                                        hasBandValueChanged(type, ["addition_fields"], "bbi_table", "bbi_table_column_configuration", "fields", type)
                                            ? "cursor-disabled"
                                            : ""
                                    }`}
                                    data-tip="Undo changes (made in this edit session) in this Band "
                                    data-for="display_formats"
                                    disabled={hasBandValueChanged(
                                        type,
                                        ["addition_fields"],
                                        "bbi_table",
                                        "bbi_table_column_configuration",
                                        "fields",
                                        type
                                    )}
                                    onClick={() => {
                                        ResetToDefault({
                                            mainKey: type,
                                            subKeys: ["addition_fields"],
                                            columnKey: "bbi_table",
                                            columnKey2: "bbi_table_column_configuration",
                                            columnKey3: "fields",
                                            band: "Building Fields"
                                        });
                                    }}
                                >
                                    <i className="fas fa-history reset text"></i>
                                </button>
                            </h2>
                        </div>
                        <DragDropContext onDragEnd={result => onColumnDnd(result, "addition_fields")}>
                            {Array.from({ length: totalRowsForColumnDnd }).map((rowData, i) => (
                                <Droppable droppableId={`droppable-${i}`} direction="horizontal">
                                    {provided => (
                                        <div ref={provided.innerRef} className="styles-sec">
                                            <div className="d-flex f-outer">
                                                {bsiProperty?.bbi_table?.bbi_table_column_configuration?.fields?.addition_fields
                                                    ?.slice(i * 3, (i + 1) * 3)
                                                    ?.map((columnData, index) => (
                                                        <Draggable
                                                            key={`draggable-${i}${index}`}
                                                            draggableId={`draggable-${i}${index}`}
                                                            index={i * 3 + index}
                                                        >
                                                            {provided => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    key={`draggable-${index}`}
                                                                    className="font-family font-st col-md-4"
                                                                >
                                                                    <div className="col-md-12 box-otr box-wide tabl-clm">
                                                                        <div className="rem-txt">
                                                                            <label className="container-check">
                                                                                {columnData?.fe_label || ""}
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={columnData?.is_active}
                                                                                    onChange={() => {
                                                                                        setBbiBsiTableColumnConfig(
                                                                                            columnData?.field_name,
                                                                                            "is_active",
                                                                                            !columnData.is_active,
                                                                                            "addition_fields",
                                                                                            type
                                                                                        );
                                                                                    }}
                                                                                />
                                                                                <span className="checkmark" />
                                                                            </label>
                                                                        </div>
                                                                        <div className="right-box-content">
                                                                            <div className="font-select">
                                                                                <span>vertical line</span>
                                                                                <select
                                                                                    className="dropdown"
                                                                                    value={columnData?.vertical_line || ""}
                                                                                    onChange={e => {
                                                                                        setBbiBsiTableColumnConfig(
                                                                                            columnData?.field_name,
                                                                                            "vertical_line",
                                                                                            !columnData.vertical_line,
                                                                                            "addition_fields",
                                                                                            type
                                                                                        );
                                                                                    }}
                                                                                >
                                                                                    <option value={false}>No</option>
                                                                                    <option value={true}>Yes</option>
                                                                                </select>
                                                                            </div>
                                                                            <div>
                                                                                <div className="ml-3">cells</div>
                                                                                <div class="input-box">
                                                                                    <NumberFormat
                                                                                        autoComplete={"nope"}
                                                                                        className="custom-input form-control"
                                                                                        value={columnData?.fill_cells || ""}
                                                                                        // suffix={" cells"}
                                                                                        thousandSeparator={true}
                                                                                        onValueChange={values => {
                                                                                            const currentValue = columnData?.fill_cells;
                                                                                            if (values.floatValue !== currentValue) {
                                                                                                const { value } = values;
                                                                                                setBbiBsiTableColumnConfig(
                                                                                                    columnData?.field_name,
                                                                                                    "fill_cells",
                                                                                                    Number(value),
                                                                                                    "addition_fields",
                                                                                                    type
                                                                                                );
                                                                                            }
                                                                                        }}
                                                                                    />
                                                                                </div>
                                                                            </div>
                                                                            <div className="image-drag">
                                                                                <img src="/img/Group 2.svg" alt="" />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    {provided.placeholder}
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                            </div>
                                        </div>
                                    )}
                                </Droppable>
                            ))}
                        </DragDropContext>
                    </>
                ) : null}
            </div>
            <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        {`${type === "bsi" ? "BSI" : "BAI"} Memo Properties`}
                        <button
                            type="button"
                            className={`reset-btn-recom ${hasBandValueChanged(type, ["memo_properties"]) ? "cursor-disabled" : ""}`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["memo_properties"])}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["memo_properties"],
                                    band: "Memo Properties"
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Apply Memo Field Property</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.memo_properties?.memo_props_active ?? ""}
                                    onChange={e => {
                                        handleBsiChange("memo_properties", "memo_props_active", e.target?.value === "true", null, null, type);
                                    }}
                                >
                                    <option value={""}>Select</option>
                                    <option value={true}>Active</option>
                                    <option value={false}>Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Heading Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.memo_properties?.heading?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("memo_properties", "font_id", e.target?.value, "heading", null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="font-size font-st col-md-3">
                            <label>Heading Font Size</label>

                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.memo_properties?.heading?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("memo_properties", "font_size", values.floatValue, "heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>

                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Heading Font Color </label>
                            </div>

                            <div className="dropdown sketch-wide">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => {
                                        handleBsiChange("memo_properties", "font_color", "", "heading", null, type);
                                    }}
                                >
                                    <i class="fas fa-times"></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() => {
                                        setColorModalState(
                                            colorModalState === "bsiProperty_memo_properties_heading_color"
                                                ? null
                                                : "bsiProperty_memo_properties_heading_color"
                                        );
                                    }}
                                >
                                    <span>{bsiProperty?.memo_properties?.heading?.font_color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.memo_properties?.heading?.font_color
                                                ? `#${bsiProperty?.memo_properties?.heading?.font_color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "bsiProperty_memo_properties_heading_color" && (
                                    <div style={styles.popover}>
                                        <div
                                            style={styles.cover}
                                            onClick={() => {
                                                setColorModalState(null);
                                            }}
                                        />
                                        <SketchPicker
                                            position="right"
                                            color={bsiProperty?.memo_properties?.heading?.font_color}
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color => {
                                                handleBsiChange("memo_properties", "font_color", color.hex?.replace("#", ""), "heading", null, type);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Heading Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.memo_properties?.heading?.font_bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("memo_properties", "font_bold", e.target?.value === "true", "heading", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Body Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.memo_properties?.body?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("memo_properties", "font_id", e.target?.value, "body", null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="font-size font-st col-md-3">
                            <label>Body Font Size</label>

                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.memo_properties?.body?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("memo_properties", "font_size", values?.floatValue, "body", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>

                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Body Font Color </label>
                            </div>

                            <div className="dropdown sketch-wide">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => {
                                        handleBsiChange("memo_properties", "font_color", "", "body", null, type);
                                    }}
                                >
                                    <i class="fas fa-times"></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() => {
                                        handleBsiChange("memo_properties", "font_color", "", "heading", null, type);
                                        setColorModalState(
                                            colorModalState === "bsiProperty_memo_properties_body_color"
                                                ? null
                                                : "bsiProperty_memo_properties_body_color"
                                        );
                                    }}
                                >
                                    <span>{bsiProperty?.memo_properties?.body?.font_color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.memo_properties?.body?.font_color
                                                ? `#${bsiProperty?.memo_properties?.body?.font_color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "bsiProperty_memo_properties_body_color" && (
                                    <div style={styles.popover}>
                                        <div
                                            style={styles.cover}
                                            onClick={() => {
                                                setColorModalState(null);
                                            }}
                                        />
                                        <SketchPicker
                                            position="right"
                                            color={bsiProperty?.memo_properties?.body?.font_color}
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color => {
                                                handleBsiChange("memo_properties", "font_color", color.hex?.replace("#", ""), "body", null, type);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Body Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.memo_properties?.body?.font_bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("memo_properties", "font_bold", e.target?.value === "true", "body", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Paragraph Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.memo_properties?.paragraph_space_before || 0}
                                    onValueChange={values => {
                                        handleBsiChange("memo_properties", "paragraph_space_before", values.floatValue, null, null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Paragraph Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.memo_properties?.paragraph_space_after || 0}
                                    onValueChange={values => {
                                        handleBsiChange("memo_properties", "paragraph_space_after", values.floatValue, null, null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Paragraph Alignment </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.memo_properties?.alignment}
                                    onChange={e => handleBsiChange("memo_properties", "alignment", e.target?.value, null, null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Paragraph Line Space </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.memo_properties?.line_space || 0}
                                    onValueChange={values => {
                                        handleBsiChange("memo_properties", "line_space", values.floatValue, null, null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="styles-box">
                <div className="outer-wrapp-resetprop">
                    <h2 className="recom-h2-sec">
                        Stoplight
                        <button
                            type="button"
                            className={`reset-btn-recom ${
                                hasBandValueChanged(type, ["custom_heading_props", "structured_heading"], "stop_light", "main_heading")
                                    ? "cursor-disabled"
                                    : ""
                            }`}
                            data-tip="Undo changes (made in this edit session) in this Band "
                            data-for="display_formats"
                            disabled={hasBandValueChanged(type, ["custom_heading_props", "structured_heading"], "stop_light", "main_heading")}
                            onClick={() => {
                                ResetToDefault({
                                    mainKey: type,
                                    subKeys: ["custom_heading_props", "structured_heading"],
                                    columnKey: "stop_light",
                                    columnKey2: "main_heading",
                                    band: "Stoplight Heading Band"
                                });
                            }}
                        >
                            <i className="fas fa-history reset text"></i>
                        </button>
                    </h2>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Stoplight Heading Label</label>
                            <div className={`input-ft-size`}>
                                <input
                                    value={bsiProperty?.stop_light?.main_heading?.name}
                                    type="text"
                                    onChange={e => {
                                        handleBsiChange("stop_light", "name", e.target?.value, "main_heading", null, type);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Stoplight Heading Type</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={isCustomHeading?.stop_light ? "custom" : "predefined"}
                                    onChange={e => {
                                        setIsCustomHeading({ ...isCustomHeading, stop_light: e.target.value === "custom" });
                                        if (e.target.value === "custom") {
                                            handleBsiChange("stop_light", null, defaultCustomHeading, "main_heading", "custom_heading_props", type);
                                        } else {
                                            handleBsiChange(
                                                "stop_light",
                                                null,
                                                { heading_style: "Heading 3" },
                                                "main_heading",
                                                "structured_heading",
                                                type
                                            );
                                        }
                                    }}
                                >
                                    <option value="custom">Custom Heading</option>
                                    <option value="predefined">Predefined Heading</option>
                                </select>
                            </div>
                        </div>
                        {isCustomHeading?.stop_light ? (
                            <>
                                <div className="font-family font-st col-md-3">
                                    <label>Stoplight Heading Font Name </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.font_id || "-"}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "stop_light",
                                                    "font_id",
                                                    e.target?.value,
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                        >
                                            <option value="">Select</option>
                                            {FONT_FAMILY.map(f => (
                                                <option key={f.id} value={f.id}>
                                                    {f.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Stoplight Heading Font Size</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.font_size || 0}
                                            onValueChange={values => {
                                                handleBsiChange(
                                                    "stop_light",
                                                    "font_size",
                                                    values.floatValue,
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                            allowNegative={false}
                                            suffix=" Pt"
                                        />
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Stoplight Heading Font Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => handleBsiChange("stop_light", "color", "", "main_heading", "custom_heading_props", type)}
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "bbi_heading_stop_light" ? null : "bbi_heading_stop_light"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.stop_light?.main_heading?.custom_heading_props?.color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.stop_light?.main_heading?.custom_heading_props?.color
                                                        ? `#${bsiProperty?.stop_light?.main_heading?.custom_heading_props?.color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "bbi_heading_stop_light" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.stop_light?.main_heading?.custom_heading_props?.color === "#000000" ||
                                                        !bsiProperty?.stop_light?.main_heading?.custom_heading_props?.color
                                                            ? ""
                                                            : bsiProperty?.stop_light?.main_heading?.custom_heading_props?.color
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "stop_light",
                                                            "color",
                                                            color.hex?.replace("#", ""),
                                                            "main_heading",
                                                            "custom_heading_props",
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Stoplight Heading Font Bold </label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.bold || "-"}
                                            onChange={e => {
                                                handleBsiChange(
                                                    "stop_light",
                                                    "bold",
                                                    e.target?.value === "true",
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                );
                                            }}
                                        >
                                            <option value={false}>No</option>
                                            <option value={true}>Yes</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-family font-st col-md-3">
                                    <label>Stoplight Heading Alignment</label>
                                    <div className="font-select">
                                        <select
                                            className="dropdown"
                                            name="tag"
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.alignment}
                                            onChange={e =>
                                                handleBsiChange(
                                                    "stop_light",
                                                    "alignment",
                                                    e.target?.value,
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                        >
                                            <option value="">Select</option>
                                            <option value={"left"}>Left</option>
                                            <option value={"center"}>Center</option>
                                            <option value={"right"}>Right</option>
                                            <option value={"justify"}>Justify</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="font-color font-st col-md-3">
                                    <div className="head_sub">
                                        <label>Stoplight Background Color </label>
                                    </div>
                                    <div className="dropdown">
                                        <span
                                            className="arrow_close"
                                            style={{ cursor: "pointer" }}
                                            onClick={e => handleBsiChange("stop_light", "bg_color", "", "main_heading", "custom_heading_props", type)}
                                        >
                                            <i class="fas fa-times "></i>
                                        </span>
                                        <div
                                            className="dropdown-toggle"
                                            type="button"
                                            onClick={() =>
                                                setColorModalState(prevColorModalState =>
                                                    prevColorModalState === "stop_light_bg_color" ? null : "stop_light_bg_color"
                                                )
                                            }
                                        >
                                            <span>{bsiProperty?.stop_light?.main_heading?.custom_heading_props?.bg_color || "Select"}</span>
                                            <span
                                                className="color-bx"
                                                style={{
                                                    background: bsiProperty?.stop_light?.main_heading?.custom_heading_props?.bg_color
                                                        ? `#${bsiProperty?.stop_light?.main_heading?.custom_heading_props?.bg_color}`
                                                        : "#fff"
                                                }}
                                            ></span>
                                            <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                        </div>
                                        {colorModalState === "stop_light_bg_color" && (
                                            <div style={styles.popover}>
                                                <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                                <SketchPicker
                                                    position="right"
                                                    color={
                                                        bsiProperty?.stop_light?.main_heading?.custom_heading_props?.bg_color === "#000000"
                                                            ? ""
                                                            : bsiProperty?.stop_light?.main_heading?.custom_heading_props?.bg_color || ""
                                                    }
                                                    presetColors={[
                                                        "#95cd50",
                                                        "#ffe242",
                                                        "#ffa105",
                                                        "#ff0305",
                                                        "#0018A8",
                                                        "#800080",
                                                        "#3E8EDE",
                                                        "#417505"
                                                    ]}
                                                    onChange={color =>
                                                        handleBsiChange(
                                                            "stop_light",
                                                            "bg_color",
                                                            color.hex?.replace("#", ""),
                                                            "main_heading",
                                                            "custom_heading_props",
                                                            type
                                                        )
                                                    }
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Stoplight Heading Margin Top</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.cell_margin?.top}
                                            onValueChange={values =>
                                                handleBsiChange(
                                                    "stop_light",
                                                    "cell_margin",
                                                    { top: values.floatValue },
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Stoplight Heading Margin Left</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.cell_margin?.left}
                                            onValueChange={values =>
                                                handleBsiChange(
                                                    "stop_light",
                                                    "cell_margin",
                                                    { left: values.floatValue },
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Stoplight Heading Margin Right</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.cell_margin?.right}
                                            onValueChange={values =>
                                                handleBsiChange(
                                                    "stop_light",
                                                    "cell_margin",
                                                    { right: values.floatValue },
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                                <div className="font-size font-st col-md-3">
                                    <label>Stoplight Heading Margin Bottom</label>
                                    <div className={`input-ft-size`}>
                                        <NumberFormat
                                            value={bsiProperty?.stop_light?.main_heading?.custom_heading_props?.cell_margin?.bottom}
                                            onValueChange={values =>
                                                handleBsiChange(
                                                    "stop_light",
                                                    "cell_margin",
                                                    { bottom: values.floatValue },
                                                    "main_heading",
                                                    "custom_heading_props",
                                                    type
                                                )
                                            }
                                            allowNegative={false}
                                            suffix=" inch"
                                        />
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="font-family font-st col-md-3">
                                <label>Stoplight Heading Style</label>
                                <div className="font-select">
                                    <select
                                        className="dropdown"
                                        value={bsiProperty?.stop_light?.main_heading?.structured_heading?.heading_style || "Heading 3"}
                                        onChange={e =>
                                            handleBsiChange("stop_light", "heading_style", e.target.value, "main_heading", "structured_heading", type)
                                        }
                                    >
                                        <option value="Heading 1">Heading 1</option>
                                        <option value="Heading 2">Heading 2</option>
                                        <option value="Heading 3">Heading 3</option>
                                    </select>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Table Column Sort Order</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.column_sort_order?.order}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "column_sort_order", { order: e.target.value }, null, null, type);
                                    }}
                                >
                                    <option value="sort_order">Sort Order</option>
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Row Sort Order</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.row_sort_order?.order}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "row_sort_order", { order: e.target.value }, null, null, type);
                                    }}
                                >
                                    <option value="sort_order">Sort Order</option>
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Blank Space</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_blank_space_config?.font_size}
                                    onValueChange={values =>
                                        handleBsiChange("stop_light", "table_blank_space_config", { font_size: values.floatValue }, null, null, type)
                                    }
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* stop light table heading #start */}
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Table Heading Label</label>
                            <div className={`input-ft-size`}>
                                <input
                                    value={bsiProperty?.stop_light?.table_heading?.label}
                                    type="text"
                                    onChange={e => {
                                        handleBsiChange("stop_light", "label", e.target?.value, "table_heading", null, type);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Heading Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_heading?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "font_id", e.target?.value, "table_heading", null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="font-size font-st col-md-3">
                            <label>Table Heading Font Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_heading?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "font_size", values.floatValue, "table_heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Table Heading Font Color </label>
                            </div>
                            <div className="dropdown">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => handleBsiChange("stop_light", "color", "", "table_heading", null, type)}
                                >
                                    <i class="fas fa-times "></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() =>
                                        setColorModalState(prevColorModalState =>
                                            prevColorModalState === "stop_light_table_font_color" ? null : "stop_light_table_font_color"
                                        )
                                    }
                                >
                                    <span>{bsiProperty?.stop_light?.table_heading?.color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.stop_light?.table_heading?.color
                                                ? `#${bsiProperty?.stop_light?.table_heading?.color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "stop_light_table_font_color" && (
                                    <div style={styles.popover}>
                                        <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                        <SketchPicker
                                            position="right"
                                            color={
                                                bsiProperty?.stop_light?.table_heading?.color === "#000000" ||
                                                !bsiProperty?.stop_light?.table_heading?.color
                                                    ? ""
                                                    : bsiProperty?.stop_light?.table_heading?.color
                                            }
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color =>
                                                handleBsiChange("stop_light", "color", color.hex?.replace("#", ""), "table_heading", null, type)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Heading Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_heading?.bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "bold", e.target?.value === "true", "table_heading", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Heading Alignment</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.stop_light?.table_heading?.alignment}
                                    onChange={e => handleBsiChange("stop_light", "alignment", e.target?.value, "table_heading", null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Heading Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_heading?.space_before || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_before", values.floatValue, "table_heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Heading Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_heading?.space_after || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_after", values.floatValue, "table_heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* stop light table heading #end */}
                {/* stop light table column heading #start */}
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Table Column Heading Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_columns_heading?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "font_id", e.target?.value, "table_columns_heading", null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="font-size font-st col-md-3">
                            <label>Table Column Heading Font Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_columns_heading?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "font_size", values.floatValue, "table_columns_heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Table Column Heading Font Color </label>
                            </div>
                            <div className="dropdown">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => handleBsiChange("stop_light", "color", "", "table_columns_heading", null, type)}
                                >
                                    <i class="fas fa-times "></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() =>
                                        setColorModalState(prevColorModalState =>
                                            prevColorModalState === "stop_light_table_column_heading_font_color"
                                                ? null
                                                : "stop_light_table_column_heading_font_color"
                                        )
                                    }
                                >
                                    <span>{bsiProperty?.stop_light?.table_columns_heading?.color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.stop_light?.table_columns_heading?.color
                                                ? `#${bsiProperty?.stop_light?.table_columns_heading?.color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "stop_light_table_column_heading_font_color" && (
                                    <div style={styles.popover}>
                                        <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                        <SketchPicker
                                            position="right"
                                            color={
                                                bsiProperty?.stop_light?.table_columns_heading?.color === "#000000" ||
                                                !bsiProperty?.stop_light?.table_columns_heading?.color
                                                    ? ""
                                                    : bsiProperty?.stop_light?.table_columns_heading?.color
                                            }
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color =>
                                                handleBsiChange(
                                                    "stop_light",
                                                    "color",
                                                    color.hex?.replace("#", ""),
                                                    "table_columns_heading",
                                                    null,
                                                    type
                                                )
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Column Heading Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_columns_heading?.bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "bold", e.target?.value === "true", "table_columns_heading", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Column Heading Alignment</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.stop_light?.table_columns_heading?.alignment}
                                    onChange={e => handleBsiChange("stop_light", "alignment", e.target?.value, "table_columns_heading", null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Column Heading Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_columns_heading?.space_before || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_before", values.floatValue, "table_columns_heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Column Heading Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_columns_heading?.space_after || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_after", values.floatValue, "table_columns_heading", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* stop light table column heading #end */}
                {/* stoplight table sub heading text color #start*/}
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Table Sub Heading Label</label>
                            <div className={`input-ft-size`}>
                                <input
                                    value={bsiProperty?.stop_light?.table_heading_text?.label}
                                    type="text"
                                    onChange={e => {
                                        handleBsiChange("stop_light", "label", e.target?.value, "table_heading_text", null, type);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Sub Heading Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_heading_text?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "font_id", e.target?.value, "table_heading_text", null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="font-size font-st col-md-3">
                            <label>Table Sub Heading Font Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_heading_text?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "font_size", values.floatValue, "table_heading_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Table Sub Heading Font Color </label>
                            </div>
                            <div className="dropdown sketch-wide">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => {
                                        handleBsiChange("stop_light", "color", "", "table_heading_text", null, type);
                                    }}
                                >
                                    <i class="fas fa-times"></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() => {
                                        setColorModalState(
                                            colorModalState === "stoplight_table_sub_heading_font_color"
                                                ? null
                                                : "stoplight_table_sub_heading_font_color"
                                        );
                                    }}
                                >
                                    <span>{bsiProperty?.stop_light?.table_heading_text?.color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.stop_light?.table_heading_text?.color
                                                ? `#${bsiProperty?.stop_light?.table_heading_text?.color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "stoplight_table_sub_heading_font_color" && (
                                    <div style={styles.popover}>
                                        <div
                                            style={styles.cover}
                                            onClick={() => {
                                                setColorModalState(null);
                                            }}
                                        />
                                        <SketchPicker
                                            position="right"
                                            color={bsiProperty?.stop_light?.table_heading_text?.color}
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color => {
                                                handleBsiChange("stop_light", "color", color.hex?.replace("#", ""), "table_heading_text", null, type);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Sub Heading Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_heading_text?.bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "bold", e.target?.value === "true", "table_heading_text", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Sub Heading Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_heading_text?.space_before || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_before", values.floatValue, "table_heading_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Sub Heading Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_heading_text?.space_after || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_after", values.floatValue, "table_heading_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Sub Heading Alignment </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.stop_light?.table_heading_text?.alignment}
                                    onChange={e => handleBsiChange("stop_light", "alignment", e.target?.value, "table_heading_text", null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
                {/* stoplight table sub heading text color #end*/}
                {/* stoplight table text #start */}
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-family font-st col-md-3">
                            <label>Table Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_text?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "font_id", e.target?.value, "table_text", null, type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="font-size font-st col-md-3">
                            <label>Table Font Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "font_size", values.floatValue, "table_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Table Font Color </label>
                            </div>
                            <div className="dropdown sketch-wide">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => {
                                        handleBsiChange("stop_light", "color", "", "table_text", null, type);
                                    }}
                                >
                                    <i class="fas fa-times"></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() => {
                                        setColorModalState(colorModalState === "stoplight_table_font_color" ? null : "stoplight_table_font_color");
                                    }}
                                >
                                    <span>{bsiProperty?.stop_light?.table_text?.color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.stop_light?.table_text?.color
                                                ? `#${bsiProperty?.stop_light?.table_text?.color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "stoplight_table_font_color" && (
                                    <div style={styles.popover}>
                                        <div
                                            style={styles.cover}
                                            onClick={() => {
                                                setColorModalState(null);
                                            }}
                                        />
                                        <SketchPicker
                                            position="right"
                                            color={bsiProperty?.stop_light?.table_text?.color}
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color => {
                                                handleBsiChange("stop_light", "color", color.hex?.replace("#", ""), "table_text", null, type);
                                            }}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Font Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_text?.bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "bold", e.target?.value === "true", "table_text", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text?.space_before || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_before", values.floatValue, "table_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text?.space_after || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_after", values.floatValue, "table_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Alignment </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.stop_light?.table_text?.alignment}
                                    onChange={e => handleBsiChange("stop_light", "alignment", e.target?.value, "table_text", null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Line Space </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text?.line_spacing || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "line_spacing", values.floatValue, "table_text", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* stoplight table text #end */}
                {/* stoplight table color icon #start*/}
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        <div className="font-size font-st col-md-3">
                            <label>Table Color Icon Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text_color?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "font_size", values.floatValue, "table_text_color", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Color Icon Bold </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.table_text_color?.bold || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "bold", e.target?.value === "true", "table_text_color", null, type);
                                    }}
                                >
                                    <option value={false}>No</option>
                                    <option value={true}>Yes</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Color Icon Space Before </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text_color?.space_before || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_before", values.floatValue, "table_text_color", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Color Icon Space After </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text_color?.space_after || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "space_after", values.floatValue, "table_text_color", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Color Icon Alignment </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.stop_light?.table_text_color?.alignment}
                                    onChange={e => handleBsiChange("stop_light", "alignment", e.target?.value, "table_text_color", null, type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Table Color Icon Line Space </label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.table_text_color?.line_spacing || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "line_spacing", values.floatValue, "table_text_color", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                {/* stoplight table color icon #end*/}
                {/* stop light legend #start */}
                <div className="styles-sec">
                    <div className="d-flex f-outer">
                        {/* stop light legends #start */}
                        <div className="font-family font-st col-md-3">
                            <label>Legend Text Font Name </label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.legends?.legends?.font_id || "-"}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "font_id", e.target?.value, "legends", "legends", type);
                                    }}
                                >
                                    <option value="">Select</option>
                                    {FONT_FAMILY.map(f => (
                                        <option key={f.id} value={f.id}>
                                            {f.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Legend Text Font Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.legends?.legends?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "legends", { font_size: values.floatValue }, "legends", null, type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        <div className="font-color font-st col-md-3">
                            <div className="head_sub">
                                <label>Legend Text Color </label>
                            </div>
                            <div className="dropdown">
                                <span
                                    className="arrow_close"
                                    style={{ cursor: "pointer" }}
                                    onClick={e => handleBsiChange("stop_light", "color", "", "legends", "legends", type)}
                                >
                                    <i class="fas fa-times "></i>
                                </span>
                                <div
                                    className="dropdown-toggle"
                                    type="button"
                                    onClick={() =>
                                        setColorModalState(prevColorModalState =>
                                            prevColorModalState === "stop_light_legend_text_font_color" ? null : "stop_light_legend_text_font_color"
                                        )
                                    }
                                >
                                    <span>{bsiProperty?.stop_light?.legends?.legends?.color || "Select"}</span>
                                    <span
                                        className="color-bx"
                                        style={{
                                            background: bsiProperty?.stop_light?.legends?.legends?.color
                                                ? `#${bsiProperty?.stop_light?.legends?.legends?.color}`
                                                : "#fff"
                                        }}
                                    ></span>
                                    <i aria-hidden="true" className="grey chevron down icon arrow_right"></i>
                                </div>
                                {colorModalState === "stop_light_legend_text_font_color" && (
                                    <div style={styles.popover}>
                                        <div style={styles.cover} onClick={() => setColorModalState(null)} />
                                        <SketchPicker
                                            position="right"
                                            color={
                                                bsiProperty?.stop_light?.legends?.legends?.color === "#000000" ||
                                                !bsiProperty?.stop_light?.legends?.legends?.color
                                                    ? ""
                                                    : bsiProperty?.stop_light?.legends?.legends?.color
                                            }
                                            presetColors={["#95cd50", "#ffe242", "#ffa105", "#ff0305", "#0018A8", "#800080", "#3E8EDE", "#417505"]}
                                            onChange={color =>
                                                handleBsiChange("stop_light", "color", color.hex?.replace("#", ""), "legends", "legends", type)
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Legend Text Order</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    value={bsiProperty?.stop_light?.legends?.legends?.order}
                                    onChange={e => {
                                        handleBsiChange("stop_light", "legends", { order: e.target.value }, "legends", type);
                                    }}
                                >
                                    <option value="sort_order">Sort Order</option>
                                    <option value="asc">Ascending</option>
                                    <option value="desc">Descending</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Legend Text Alignment</label>
                            <div className="font-select">
                                <select
                                    className="dropdown"
                                    name="tag"
                                    value={bsiProperty?.stop_light?.legends?.legends?.alignment}
                                    onChange={e => handleBsiChange("stop_light", "alignment", e.target?.value, "legends", "legends", type)}
                                >
                                    <option value="">Select</option>
                                    <option value={"left"}>Left</option>
                                    <option value={"center"}>Center</option>
                                    <option value={"right"}>Right</option>
                                    <option value={"justify"}>Justify</option>
                                </select>
                            </div>
                        </div>
                        <div className="font-family font-st col-md-3">
                            <label>Legend Icon Size</label>
                            <div className={`input-ft-size`}>
                                <NumberFormat
                                    value={bsiProperty?.stop_light?.legends?.color?.font_size || 0}
                                    onValueChange={values => {
                                        handleBsiChange("stop_light", "color", { font_size: values.floatValue }, "legends", type);
                                    }}
                                    allowNegative={false}
                                    suffix=" Pt"
                                />
                            </div>
                        </div>
                        {/* stop light legends #end */}
                    </div>
                </div>
            </div>
        </>
    );
};

export default BsiForm;
